import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { Button, Badge, Avatar } from "components/ui";
import {
  Select as FormSelect,
  Radio,
  Checkbox,
  Input,
  Textarea,
} from "components/ui/Form";
import ReactSelect from "react-select";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  Cog6ToothIcon,
  UserPlusIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  CheckIcon,
  UsersIcon,
  BriefcaseIcon,
  PencilIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

// Import scratch overlays
import { DatePicker } from "components/shared/form/Datepicker";
import { PersonnelSection } from "../new/scratch/components/PersonnelSection";
import { AddContactModal } from "../new/scratch/components/AddContactModal";
import { ManageJobTypesModal } from "../new/scratch/components/ManageJobTypesModal";
import { AdvancedSettingsDrawer } from "../new/scratch/components/AdvancedSettingsDrawer";
import AssignContactsToLeadModal from "components/leads/LeadDetails/AssignContactsToLeadModal";
import ContactModal from "app/pages/settings/sections/ContactsManagement/ContactModal";
import { CustomPagination } from "components/shared/table/CustomPagination";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown.jsx";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { FieldSelectionModal } from "app/pages/settings/sections/builder/FieldSelectionModal";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { AttachmentsActions } from "components/shared/AttachmentsActions";
import {
  getJobGroups,
  createJobGroup,
  deleteJobGroup,
  getContractors,
  getMasterFields,
  convertLeadToJob,
  getInternalUsers,
  getProjectManagers,
  getJobStatuses,
  autocompleteJobTitle,
  getJobById,
} from "services/jobsService";
import { getCompanyIdFromToken, getUserIdFromToken } from "utils/tokenUtils";
import {
  getUsersForAssignLeads,
  getAllUsersWithDesignations,
} from "services/userService";
import { updateLeadOwner } from "services/leadsService";
import { upsertMasterField } from "services/formBuilderService";
import {
  addEditLeadSource,
  deleteLeadSource,
} from "services/leadsourcesServices";
import axiosInstance from "utils/axios";
import { toast } from "sonner";
import { useDropdown, useSuburbRecommendation } from "hooks";
import { DropdownEnum } from "enum/DropdownEnum";
import { canManageJobStatus } from "permissions/permissionHelpers/jobPermissions";
import { ManageStatusContent } from "./SetJobStatusModal";
import { Dialog, DialogPanel, Transition, TransitionChild, Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { Fragment } from "react";
import { invalidateJobGroupsCache } from "components/leads/LeadFilters";
import { uploadAttachments, DocumentUploadFeature } from "services/documentUploadService";
import { useAuthContext } from "app/contexts/auth/context";

function ThemeSelect({
  label,
  data,
  value,
  onChange,
  error,
  required,
  onMenuOpen,
}) {
  const options = data.map((item) => {
    if (typeof item === "object") return item;
    return { value: item, label: item };
  });

  const selectedValue = options.find((opt) => opt.value === value) || null;

  return (
    <div className="input-root">
      {label && (
        <label className="input-label flex items-center gap-1">
          <span className="input-label">{label}</span>
          {required && <span className="text-error">*</span>}
        </label>
      )}
      <div className={clsx("w-full", label && "mt-1.5")}>
        <ReactSelect
          options={options}
          value={selectedValue}
          onChange={(opt) => onChange(opt?.value || "")}
          onMenuOpen={onMenuOpen}
          className="react-select-container w-full"
          classNamePrefix="react-select"
          isClearable={false}
          isSearchable={false}
        />
      </div>
      {error && <p className="text-error mt-1 text-xs font-medium">{error}</p>}
    </div>
  );
}

const STATUS_ID_MAP = {
  Pending: 1,
  Ongoing: 2,
  "Completed/Warranty": 3,
  Archive: 4,
  "Under Approval": 5,
};

const CONTRACT_TYPE_MAP = {
  "Fixed price": 0,
  "Open book": 1,
};

const DAY_ID_MAP = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export default function CreateJobFromScratchForm({
  leadData,
  prefillData,
  onSubmit,
  onBack,
  onClose,
  isConverting = false,
  hasError = false,
}) {
  const { user: authUser } = useAuthContext();
  const [step, setStep] = useState(3); // Form steps start at 3

  useEffect(() => {
    // Scroll window
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Scroll any modal or scrollable containers
    const scrollableContainers = document.querySelectorAll(
      ".overflow-y-auto, .overflow-auto",
    );
    scrollableContainers.forEach((container) => {
      container.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [step]);

  // Form States
  const [title, setTitle] = useState("");
  // Possible-duplicate job suggestions while typing the title -- a sales user (or anyone without
  // JOB_VIEW_ALL) can't see every job in the company, so without this they have no way to tell
  // whether a job for this address already exists before creating a second one. Deliberately
  // unfiltered by the viewer's own job-visibility permissions (see AutocompleteJobTitle.sql) --
  // opening a suggestion they don't have access to is handled by the job detail page itself.
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState([]);
  const [showJobTitleSuggestions, setShowJobTitleSuggestions] = useState(false);
  const [isSearchingJobTitle, setIsSearchingJobTitle] = useState(false);
  const [checkingViewJobId, setCheckingViewJobId] = useState(null);
  const jobTitleSearchTimeoutRef = useRef(null);

  const handleTitleChange = (value) => {
    setTitle(value);

    if (jobTitleSearchTimeoutRef.current) {
      clearTimeout(jobTitleSearchTimeoutRef.current);
    }

    const trimmed = (value || "").trim();
    // Job titles like "7/109 Denman Prospect" are often typed as just 1-2 characters before the
    // user pauses, so this can't wait for a long prefix the way most search boxes do.
    if (trimmed.length < 2) {
      setJobTitleSuggestions([]);
      setShowJobTitleSuggestions(false);
      setIsSearchingJobTitle(false);
      return;
    }

    setIsSearchingJobTitle(true);
    setShowJobTitleSuggestions(true);

    jobTitleSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await autocompleteJobTitle(trimmed);
        setJobTitleSuggestions(res.success ? res.data : []);
      } catch (err) {
        console.error("Job title autocomplete error:", err);
        setJobTitleSuggestions([]);
      } finally {
        setIsSearchingJobTitle(false);
      }
    }, 400);
  };

  // Checks the viewer's own permission for the suggested job before opening it, so a denied
  // viewer sees the reason inline in this modal instead of being bounced through /jobs/:jobId
  // (which redirects back to /jobs on denial -- see JobsList.jsx's getJobById handling).
  const handleViewExistingJob = async (job) => {
    setCheckingViewJobId(job.JobId);
    try {
      const res = await getJobById(job.JobId);
      if (res.success) {
        window.open(`/jobs/${job.JobId}`, "_blank", "noopener,noreferrer");
      } else {
        toast.error(res.message || "You are not assigned to this job, so you do not have access to it.");
      }
    } catch (err) {
      console.error("Error checking job view permission:", err);
      toast.error("Unable to check access to this job. Please try again.");
    } finally {
      setCheckingViewJobId(null);
    }
  };
  const [prefix, setPrefix] = useState("");
  const [jobStatus, setJobStatus] = useState(0);
  const [jobStatuses, setJobStatuses] = useState([]);
  const [hasFetchedStatuses, setHasFetchedStatuses] = useState(false);
  const [contractPrice, setContractPrice] = useState("");
  const [jobType, setJobType] = useState("Custom New Home");
  const [contractType, setContractType] = useState("Fixed price");

  const handleFetchStatuses = async (silent = false) => {
    if (!hasFetchedStatuses || silent) {
      try {
        const res = await getJobStatuses();
        if (res.success) {
          const formatted = res.data.map((s) => ({
            label: s.StatusName || s.name || s.label,
            alias: s.Alias || s.StatusName || s.name || s.label || "",
            value: s.StatusId || s.id || s.value,
            type: s.IsSystem ? "System" : "Custom",
            jobCount: s.JobCount || 0,
          }));
          setJobStatuses(formatted);
          setHasFetchedStatuses(true);
        }
      } catch (error) {
        console.error("Error fetching job statuses:", error);
      }
    }
  };

  const [addressVal, setAddressVal] = useState("");
  const [suburbVal, setSuburbVal] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [postalCodeVal, setPostalCodeVal] = useState("");

  const {
    options: suburbOptions,
    isLoading: isLoadingSuburbs,
    onCreateOption: handleCreateSuburb,
    onEditOption: handleEditSuburb,
    onDeleteOption: handleDeleteSuburb,
  } = useDropdown(DropdownEnum.Suburb);

  // Autofill State + PostalCode when the selected/typed Suburb matches a known
  // recommendation. Never fires for State-only entry — only Suburb resolves State/PostalCode.
  const lookupSuburbRecommendation = useSuburbRecommendation((rec) => {
    setStateVal(rec.state || "");
    setPostalCodeVal(rec.postalCode || "");
  });

  const [projectedStart, setProjectedStart] = useState("");
  const [actualStart, setActualStart] = useState("");
  const [projectedCompletion, setProjectedCompletion] = useState("");
  const [actualCompletion, setActualCompletion] = useState("");
  const [updateActualDates, setUpdateActualDates] = useState(false);
  const [scheduleColor, setScheduleColor] = useState("Rose");
  const [workDays, setWorkDays] = useState([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ]);

  const [jobGroup, setJobGroup] = useState("-- Please Select --");
  const [projectManagers, setProjectManagers] = useState([]);
  const prevProjectManagersRef = useRef([]);
  const [projectManagersList, setProjectManagersList] = useState([
    "John Doe",
    "Jane Smith",
    "Richa Keswani",
  ]);
  const [fundedByLoan, setFundedByLoan] = useState("No");
  const [squareMeters, setSquareMeters] = useState("");
  const [permitNumber, setPermitNumber] = useState("");

  const [clientsList, setClientsList] = useState([]);
  const [internalUsersList, setInternalUsersList] = useState([]);
  const [pmObjectsList, setPmObjectsList] = useState([]);
  const [assignedOwnersList, setAssignedOwnersList] = useState([]);
  const [showDesignationConfirmModal, setShowDesignationConfirmModal] = useState(false);
  const [hasSkippedDesignationModal, setHasSkippedDesignationModal] = useState(false);
  const [vendorsList, setVendorsList] = useState([]);
  const [selectedVendorsList, setSelectedVendorsList] = useState([]);
  const [expandedTradeOptions, setExpandedTradeOptions] = useState(new Set());
  const toggleTradeExpansion = (optionId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedTradeOptions((prev) => {
      const next = new Set(prev);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        next.add(optionId);
      }
      return next;
    });
  };

  // Custom Fields Step
  const [masterFieldCategories, setMasterFieldCategories] = useState([]);
  const [masterFieldsLoading, setMasterFieldsLoading] = useState(false);
  const [selectedCustomFields, setSelectedCustomFields] = useState([]); // [{id, label, type, value}]
  const [cfSearchQuery, setCfSearchQuery] = useState("");
  const [dynamicOptions, setDynamicOptions] = useState({});

  const [internalNotes, setInternalNotes] = useState("");
  const [vendorInstructions, setVendorInstructions] = useState("");

  const [errors, setErrors] = useState({});

  // Overlay States
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showSelectContact, setShowSelectContact] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showManageJobTypes, setShowManageJobTypes] = useState(false);
  const [showManageStatuses, setShowManageStatuses] = useState(false);
  const [showFieldSelectionModal, setShowFieldSelectionModal] = useState(false);

  const internalUsersEndRef = useRef(null);
  const contractorsEndRef = useRef(null);

  // Job Groups state & logic
  const [jobGroups, setJobGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const res = await getJobGroups();
      if (res.success && res.data) {
        let rawData = res.data;
        if (typeof rawData === "string") {
          try {
            rawData = JSON.parse(rawData);
          } catch (e) {
            console.error("Failed to parse raw job groups JSON string:", e);
          }
        }
        let groupsArray = rawData.data || rawData.Data || rawData;
        if (typeof groupsArray === "string") {
          try {
            groupsArray = JSON.parse(groupsArray);
          } catch (e) {
            console.error("Failed to parse groupsArray JSON string:", e);
          }
        }
        if (Array.isArray(groupsArray)) {
          const mapped = groupsArray
            .map((g) => ({
              value: g.GroupId || g.groupId || "",
              label: g.GroupName || g.groupName || "",
            }))
            .filter((g) => g.value && g.label);
          setJobGroups(mapped);
        }
      }
    } catch (err) {
      console.error("Error loading job groups:", err);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const [hideWorkerBoys, setHideWorkerBoys] = useState(true);

  const fetchInternalUsers = async (hideWorkerBoysOverride = hideWorkerBoys) => {
    try {
      const companyId = getCompanyIdFromToken();
      const res = await getAllUsersWithDesignations(companyId, hideWorkerBoysOverride);
      if (res.success && res.data) {
        const mappedUsers = res.data.map((u, idx) => ({
          id: u.UserId || u.userId || u.id || idx,
          label: u.FullName || u.fullName || u.label || "",
          roleName: u.RoleName || u.roleName || "",
          designation: u.DesignationName || u.designationName || "",
          designations: u.designations || u.Designations || [],
          initial: (u.FullName || u.fullName || u.label || "U")
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2),
        }));
        setInternalUsersList(mappedUsers);
      }
    } catch (err) {
      console.error("Error loading internal users:", err);
    }
  };

  const fetchContractors = async () => {
    try {
      const res = await getContractors();
      if (res.success && res.data) {
        let rawData = res.data;
        if (typeof rawData === "string") {
          try {
            rawData = JSON.parse(rawData);
          } catch (e) {
            console.error("Failed to parse raw contractors JSON:", e);
          }
        }
        let contractorsArray = rawData.data || rawData.Data || rawData;
        if (typeof contractorsArray === "string") {
          try {
            contractorsArray = JSON.parse(contractorsArray);
          } catch (e) { }
        }
        if (Array.isArray(contractorsArray)) {
          // TODO: remove these demo fallbacks once backend returns designation & TradeTypes

          const mappedVendors = contractorsArray.map((c, idx) => {
            const name =
              c.companyName ||
              c.CompanyName ||
              c.contractorName ||
              c.ContractorName ||
              c.fullName ||
              c.FullName ||
              c.name ||
              c.label ||
              "";
            const externalCompanyName = c.externalCompanyName || "";
            // Extract designation name (fallback to demo data)
            const designation = c.DesignationName;
            const designations = c.Designations || c.designations || [];
            // Extract trade types as an array of strings (fallback to demo data)
            const rawTradeTypes = c.TradeTypes || [];
            const tradeTypes =
              Array.isArray(rawTradeTypes) && rawTradeTypes.length > 0
                ? rawTradeTypes
                  .map((t) =>
                    typeof t === "string"
                      ? t
                      : t.OptionName || t.Name || t.name || "",
                  )
                  .filter(Boolean)
                : typeof c.TradeCategory === "string" && c.TradeCategory
                  ? c.TradeCategory.split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                  : [];
            return {
              id:
                c.contractorId ||
                c.ContractorId ||
                c.userId ||
                c.UserId ||
                c.id ||
                idx,
              label: name,
              externalCompanyName:
                externalCompanyName !== name ? externalCompanyName : "",
              designation,
              designations,
              tradeTypes,
              initial:
                name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .substring(0, 2) || "C",
            };
          });
          setVendorsList(mappedVendors);
        }
      }
    } catch (err) {
      console.error("Error loading contractors:", err);
    }
  };

  const fetchMasterFieldCategories = async () => {
    setMasterFieldsLoading(true);
    try {
      // Jobs is a Lead/Job context -- never show Selection-only master fields here.
      const res = await getMasterFields(false);
      if (res.success && Array.isArray(res.data)) {
        setMasterFieldCategories(res.data);
      }
    } catch (err) {
      console.error("Error loading master fields:", err);
    } finally {
      setMasterFieldsLoading(false);
    }
  };

  const fetchProjectManagersList = async () => {
    try {
      const res = await getProjectManagers();
      if (res.success && res.data) {
        const mappedPMs = res.data.map((u, idx) => ({
          id: u.UserId || u.userId || u.id || idx,
          label: u.FullName || u.fullName || u.label || "",
          roleName: u.RoleName || u.roleName || "",
          designation: u.DesignationName || u.designationName || u.RoleName || u.roleName || "",
          designations: u.Designations || u.designations || [],
          initial: (u.FullName || u.fullName || u.label || "U")
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2),
        }));
        setPmObjectsList(mappedPMs);

        const names = mappedPMs.map((u) => u.label);
        setProjectManagersList(names);

        setProjectManagers((prev) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          if (
            !prevArray ||
            prevArray.length === 0 ||
            (prevArray.length === 1 && prevArray[0] === "John Doe") ||
            !prevArray.every((p) => names.includes(p))
          ) {
            return [];
          }
          return prevArray;
        });
      }
    } catch (err) {
      console.error("Error loading project managers:", err);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchInternalUsers(hideWorkerBoys);
    fetchProjectManagersList();
    fetchContractors();
    fetchMasterFieldCategories();
  }, []);

  useEffect(() => {
    fetchInternalUsers(hideWorkerBoys);
  }, [hideWorkerBoys]);

  useEffect(() => {
    const currentUserId = authUser?.UserId || authUser?.userId || authUser?.id || getUserIdFromToken();
    if (currentUserId && internalUsersList.length > 0 && assignedOwnersList.length === 0) {
      const loggedInUser = internalUsersList.find(
        (u) => String(u.id) === String(currentUserId)
      );
      if (loggedInUser) {
        const desigs = loggedInUser.designations || [];
        const defaultDesignationId = desigs.length === 1
          ? (desigs[0].DesignationId || desigs[0].designationId)
          : null;
        const defaultUser = {
          id: loggedInUser.id,
          label: loggedInUser.label,
          designation: loggedInUser.designation,
          designations: desigs,
          designationIds: defaultDesignationId ? [defaultDesignationId] : [],
          initial: loggedInUser.initial,
          roleName: loggedInUser.roleName,
        };
        setAssignedOwnersList([defaultUser]);
        setProjectManagersList([loggedInUser.label]);
      }
    }
  }, [internalUsersList, authUser]);

  // Helper to keep project managers synced into assignedOwnersList (handling both addition and removal)
  const syncPMsToAssignedOwners = (currentPMs, prevPMs = []) => {
    const removedPMs = prevPMs.filter((pm) => !currentPMs.includes(pm));

    setAssignedOwnersList((prevAssigned) => {
      let changed = false;
      let updated = [...prevAssigned];

      // Handle removed project managers
      const usersToRemove = new Set();
      const userDesignationUpdates = new Map();

      removedPMs.forEach((pmName) => {
        const pmUser =
          internalUsersList.find((u) => u.label === pmName) ||
          pmObjectsList.find((u) => u.label === pmName);

        const existing = updated.find(
          (u) => u.label === pmName || (pmUser && String(u.id) === String(pmUser.id))
        );

        if (existing) {
          const desigs = existing.designations || pmUser?.designations || [];
          const pmDesig = desigs.find((d) =>
            /project\s*manager/i.test(d.DesignationName || d.designationName || "")
          );
          const pmDesigId = pmDesig ? (pmDesig.DesignationId || pmDesig.designationId) : null;
          const currentDesigIds = Array.isArray(existing.designationIds) ? existing.designationIds : [];
          const remainingDesigIds = pmDesigId
            ? currentDesigIds.filter((id) => String(id) !== String(pmDesigId))
            : [];

          const currentUserId = authUser?.UserId || authUser?.userId || authUser?.id || getUserIdFromToken();
          const isCreator = currentUserId && String(existing.id) === String(currentUserId);

          // If the user has no remaining designations and was not the default creator, remove them from internal users
          if (remainingDesigIds.length === 0 && !isCreator) {
            usersToRemove.add(existing.id);
            changed = true;
          } else if (pmDesigId && currentDesigIds.some((id) => String(id) === String(pmDesigId))) {
            userDesignationUpdates.set(existing.id, remainingDesigIds);
            changed = true;
          }
        }
      });

      if (usersToRemove.size > 0 || userDesignationUpdates.size > 0) {
        updated = updated
          .filter((u) => !usersToRemove.has(u.id))
          .map((u) => {
            if (userDesignationUpdates.has(u.id)) {
              return {
                ...u,
                designationIds: userDesignationUpdates.get(u.id),
              };
            }
            return u;
          });
      }

      // Handle added project managers
      currentPMs.forEach((pmName) => {
        const pmUser =
          internalUsersList.find((u) => u.label === pmName) ||
          pmObjectsList.find((u) => u.label === pmName);
        if (!pmUser) return;

        const desigs = pmUser.designations || [];
        const pmDesig = desigs.find((d) =>
          /project\s*manager/i.test(d.DesignationName || d.designationName || "")
        );
        const pmDesigId = pmDesig ? (pmDesig.DesignationId || pmDesig.designationId) : null;

        const existingIndex = updated.findIndex(
          (u) => String(u.id) === String(pmUser.id) || u.label === pmUser.label
        );

        if (existingIndex >= 0) {
          const existing = updated[existingIndex];
          const currentDesigIds = Array.isArray(existing.designationIds) ? existing.designationIds : [];
          const hasPmDesig = pmDesigId && currentDesigIds.some((id) => String(id) === String(pmDesigId));

          if (pmDesigId && !hasPmDesig) {
            updated[existingIndex] = {
              ...existing,
              designations: existing.designations?.length ? existing.designations : desigs,
              designationIds: [...currentDesigIds, pmDesigId],
            };
            changed = true;
          }
        } else {
          updated.push({
            id: pmUser.id,
            label: pmUser.label,
            designation: pmUser.designationName || pmUser.designation || "Project Manager",
            designations: desigs,
            designationIds: pmDesigId ? [pmDesigId] : [],
            initial: pmUser.initial,
            roleName: pmUser.roleName,
          });
          changed = true;
        }
      });

      return changed ? updated : prevAssigned;
    });
  };

  // Keep project managers synced into assignedOwnersList with Project Manager designation selected
  useEffect(() => {
    if (internalUsersList.length === 0 && pmObjectsList.length === 0) return;

    const prevPMs = prevProjectManagersRef.current || [];
    const currentPMs = Array.isArray(projectManagers) ? projectManagers : [];
    prevProjectManagersRef.current = currentPMs;

    syncPMsToAssignedOwners(currentPMs, prevPMs);
  }, [projectManagers, internalUsersList, pmObjectsList, authUser]);

  useEffect(() => {
    if (masterFieldCategories.length === 0) return;

    // Load saved custom fields from localStorage for EntityTypeCode = 2 (Job)
    try {
      const savedFields = JSON.parse(localStorage.getItem("dynamic_fields_pref_2") || "[]");
      if (savedFields.length > 0) {
        setSelectedCustomFields((prev) => {
          let newFields = [...prev];
          let added = false;
          savedFields.forEach(fieldName => {
            if (!newFields.some(f => (f.fieldName || f.FieldName || "").toLowerCase() === (fieldName || "").toLowerCase())) {
              for (const cat of masterFieldCategories) {
                const found = cat.MasterFields?.find(f => (f.FieldName || f.fieldName || "").toLowerCase() === (fieldName || "").toLowerCase());
                if (found) {
                  newFields.push({
                    id: found.Id || found.id || Date.now(),
                    label: found.FieldLabel || found.label || "",
                    fieldName: found.FieldName || found.fieldName || "",
                    type: found.HtmlInputType || found.type || "text",
                    options: found.options || found.MasterFormFieldOptions || [],
                    value: found.DefaultValue || found.defaultValue || "",
                    FieldTypeId: found.FieldTypeId || found.fieldTypeId || (found.HtmlInputType === "select" ? 7 : 1),
                    fieldTypeId: found.FieldTypeId || found.fieldTypeId || (found.HtmlInputType === "select" ? 7 : 1),
                    DefaultValue: found.DefaultValue || found.defaultValue || "",
                    defaultValue: found.DefaultValue || found.defaultValue || "",
                    MasterFieldId: found.Id || found.id,
                    masterFieldId: found.Id || found.id,
                    FieldName: found.FieldName || found.fieldName || "",
                    FieldLabel: found.FieldLabel || found.label || "",
                    MasterFormFieldOptions: found.options || found.MasterFormFieldOptions || [],
                    CategoryId: found.CategoryId || cat.Id,
                    CategoryName: found.CategoryName || cat.CategoryName,
                  });
                  added = true;
                  break;
                }
              }
            }
          });
          return added ? newFields : prev;
        });
      }
    } catch (e) { }

    // Find all API-driven select fields (FieldTypeId 13) that have a DefaultValue endpoint
    const apiFields = masterFieldCategories
      .flatMap((cat) => cat.MasterFields || [])
      .filter(
        (f) =>
          f.FieldTypeId === 13 &&
          f.DefaultValue &&
          (!f.MasterFormFieldOptions || f.MasterFormFieldOptions.length === 0),
      );

    if (apiFields.length === 0) return;

    apiFields.forEach(async (f) => {
      const endpoint = f.DefaultValue.startsWith("/")
        ? f.DefaultValue
        : `/${f.DefaultValue}`;
      try {
        const res = await axiosInstance.get(endpoint, {
          params: { pageNumber: 1, PageSize: 10000, Search: "" },
        });
        let raw = res.data?.data ?? res.data;
        if (typeof raw === "string") {
          try {
            raw = JSON.parse(raw);
          } catch (_) {
            raw = [];
          }
        }
        const list = Array.isArray(raw) ? raw : [];
        const normalised = list.map((item) => ({
          label: item.SourceName,
          value: item.SourceId,
        }));
        setDynamicOptions((prev) => ({ ...prev, [f.FieldName]: normalised }));
      } catch (err) {
        console.error(
          `[CreateJobFromScratchForm] Failed to fetch options for "${f.FieldName}" from "${endpoint}":`,
          err,
        );
      }
    });
  }, [masterFieldCategories]);

  // Helper to resolve the correct CategoryId
  const getResolvedCategoryId = (fieldObj) => {
    const fieldCatId = fieldObj.CategoryId || fieldObj.categoryId;
    if (
      fieldCatId &&
      typeof fieldCatId === "string" &&
      !fieldCatId.toLowerCase().startsWith("step")
    ) {
      return fieldCatId;
    }

    const masterId = fieldObj.MasterFieldId || fieldObj.id || fieldObj.Id;
    const fieldName =
      fieldObj.FieldName || fieldObj.fieldName || fieldObj.label;

    if (masterFieldCategories && masterFieldCategories.length > 0) {
      for (const category of masterFieldCategories) {
        const found = category.MasterFields?.some(
          (f) =>
            (masterId && (f.Id === masterId || f.MasterFieldId === masterId)) ||
            (fieldName &&
              f.FieldName?.toLowerCase() === fieldName.toLowerCase()),
        );
        if (found) return category.Id;
      }
    }
    return null;
  };

  const getFieldOptions = (field) => {
    if (!field) return [];
    const isDynamic = field.FieldTypeId === 13 || field.fieldTypeId === 13;
    if (isDynamic) {
      const fieldNameBase = (field.FieldName || field.fieldName || "").replace(
        /_\d+$/,
        "",
      );
      return (
        dynamicOptions[field.FieldName || field.fieldName] ||
        dynamicOptions[fieldNameBase] ||
        []
      );
    } else {
      return (field.MasterFormFieldOptions || field.options || []).map((o) => ({
        label: o.OptionLabel || o.optionLabel || o.label || "",
        value: o.OptionValue || o.optionValue || o.value || "",
        id: o.Id || o.OptionId || o.optionId || "",
      }));
    }
  };

  const handleCreateDropdownOption = async (field, optionLabel) => {
    if (!optionLabel.trim() || !field) return;

    const optionValue = optionLabel.trim();

    // Check for duplicates (case-insensitive)
    const currentOptions = getFieldOptions(field);
    const isDuplicate = currentOptions.some(
      (opt) => (opt.label || "").toLowerCase() === optionLabel.toLowerCase(),
    );
    if (isDuplicate) {
      toast.error("This option already exists");
      return;
    }

    try {
      const isLeadSource = field.FieldTypeId === 13 || field.fieldTypeId === 13;

      if (isLeadSource) {
        const response = await addEditLeadSource({ sourceName: optionLabel });
        if (response.success) {
          toast.success("Lead source added successfully");

          let parsedData = response.data;
          if (typeof parsedData === "string") {
            try {
              parsedData = JSON.parse(parsedData);
            } catch (_) { }
          }
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            parsedData = parsedData[0];
          }

          const newSourceId =
            parsedData?.SourceId ||
            parsedData?.sourceId ||
            parsedData?.Id ||
            parsedData?.id ||
            optionLabel;

          setDynamicOptions((prev) => {
            const fieldNameBase = (
              field.FieldName ||
              field.fieldName ||
              ""
            ).replace(/_\d+$/, "");
            const baseOpts = prev[fieldNameBase] || [];
            const specificOpts = prev[field.FieldName || field.fieldName] || [];
            const updatedBase = [
              ...baseOpts,
              { label: optionLabel, value: newSourceId },
            ];
            const updatedSpecific = [
              ...specificOpts,
              { label: optionLabel, value: newSourceId },
            ];
            return {
              ...prev,
              [field.FieldName || field.fieldName]: updatedSpecific,
              [fieldNameBase]: updatedBase,
            };
          });

          // Set the value of the custom field
          setSelectedCustomFields((prev) =>
            prev.map((f) =>
              f.id === field.id ? { ...f, value: newSourceId } : f,
            ),
          );
        } else {
          toast.error(response.error || "Failed to save new option");
        }
      } else {
        const existingOptions =
          field.MasterFormFieldOptions || field.options || [];
        const updatedOptions = [
          ...existingOptions.map((opt, idx) => ({
            Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
            OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
            OptionValue: opt.OptionValue || opt.optionValue || opt.value,
            OptionOrder:
              opt.OptionOrder || opt.optionOrder || opt.order || idx + 1,
          })),
          {
            Id: crypto.randomUUID
              ? crypto.randomUUID()
              : `option-${Date.now()}`,
            OptionLabel: optionLabel,
            OptionValue: optionValue,
            OptionOrder: existingOptions.length + 1,
          },
        ];

        const categoryId = getResolvedCategoryId(field);

        const payload = {
          id: field.MasterFieldId || field.id || field.Id,
          fieldName: field.FieldName || field.fieldName,
          fieldLabel: field.FieldLabel || field.label,
          fieldTypeId: field.FieldTypeId || field.fieldTypeId || 7,
          isMendatory: field.IsMendatory || field.isMendatory || false,
          placeholder: field.Placeholder || field.placeholder || field.label,
          isActive: true,
          defaultValue: field.DefaultValue || field.defaultValue || "",
          fieldOrder: field.FieldOrder || field.fieldOrder || 0,
          options: updatedOptions,
          isCommon: true,
          categoryId: categoryId,
          isDeleted: false,
        };

        const response = await upsertMasterField(payload);
        if (response.success) {
          toast.success("Option added successfully");

          const parsedData =
            typeof response.data?.data === "string"
              ? JSON.parse(response.data.data)
              : response.data?.data;
          const savedOptions =
            parsedData?.MasterFormFieldOptions ||
            parsedData?.options ||
            updatedOptions;

          // Update option lists locally in selectedCustomFields and masterFieldCategories
          setSelectedCustomFields((prev) =>
            prev.map((f) => {
              const matches =
                f.fieldName === field.fieldName || f.id === field.id;
              if (matches) {
                return {
                  ...f,
                  options: savedOptions,
                  MasterFormFieldOptions: savedOptions,
                  value: optionValue,
                };
              }
              return f;
            }),
          );

          setMasterFieldCategories((prev) =>
            prev.map((cat) => ({
              ...cat,
              MasterFields: cat.MasterFields?.map((f) => {
                const matches =
                  f.FieldName === field.fieldName || f.Id === field.id;
                if (matches) {
                  return {
                    ...f,
                    MasterFormFieldOptions: savedOptions,
                    options: savedOptions,
                  };
                }
                return f;
              }),
            })),
          );
        } else {
          toast.error(response.message || "Failed to save new option");
        }
      }
    } catch (error) {
      console.error("Error saving option:", error);
      toast.error("An error occurred while saving option");
    }
  };

  const handleEditDropdownOption = async (
    field,
    optionValue,
    oldLabel,
    newLabelText,
  ) => {
    if (!newLabelText.trim() || !field) return;

    const optionLabel = newLabelText.trim();
    const newOptionValue = optionLabel;

    const isLeadSource = field.FieldTypeId === 13 || field.fieldTypeId === 13;

    try {
      if (isLeadSource) {
        const response = await addEditLeadSource({
          id: optionValue,
          sourceName: optionLabel,
        });
        if (response.success) {
          toast.success("Lead source updated successfully");

          setDynamicOptions((prev) => {
            const fieldNameBase = (
              field.FieldName ||
              field.fieldName ||
              ""
            ).replace(/_\d+$/, "");
            const updateList = (list) =>
              list.map((opt) =>
                String(opt.value) === String(optionValue)
                  ? { ...opt, label: optionLabel }
                  : opt,
              );
            return {
              ...prev,
              [field.FieldName || field.fieldName]: updateList(
                prev[field.FieldName || field.fieldName] || [],
              ),
              [fieldNameBase]: updateList(prev[fieldNameBase] || []),
            };
          });
        } else {
          toast.error(response.error || "Failed to update option");
        }
      } else {
        const existingOptions =
          field.MasterFormFieldOptions || field.options || [];
        const updatedOptions = existingOptions.map((opt) => {
          const matches =
            String(opt.OptionValue || opt.optionValue || opt.value)
              .trim()
              .toLowerCase() === String(optionValue).trim().toLowerCase();
          if (matches) {
            return {
              Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
              OptionLabel: optionLabel,
              OptionValue: newOptionValue,
              OptionOrder: opt.OptionOrder || opt.optionOrder || opt.order || 1,
            };
          }
          return {
            Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
            OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
            OptionValue: opt.OptionValue || opt.optionValue || opt.value,
            OptionOrder: opt.OptionOrder || opt.optionOrder || opt.order || 1,
          };
        });

        const categoryId = getResolvedCategoryId(field);

        const payload = {
          id: field.MasterFieldId || field.id || field.Id,
          fieldName: field.FieldName || field.fieldName,
          fieldLabel: field.FieldLabel || field.label,
          fieldTypeId: field.FieldTypeId || field.fieldTypeId || 7,
          isMendatory: field.IsMendatory || field.isMendatory || false,
          placeholder: field.Placeholder || field.placeholder || field.label,
          isActive: true,
          defaultValue: field.DefaultValue || field.defaultValue || "",
          fieldOrder: field.FieldOrder || field.fieldOrder || 0,
          options: updatedOptions,
          isCommon: true,
          categoryId: categoryId,
          isDeleted: false,
        };

        const response = await upsertMasterField(payload);
        if (response.success) {
          toast.success("Option updated successfully");

          const parsedData =
            typeof response.data?.data === "string"
              ? JSON.parse(response.data.data)
              : response.data?.data;
          const savedOptions =
            parsedData?.MasterFormFieldOptions ||
            parsedData?.options ||
            updatedOptions;

          setSelectedCustomFields((prev) =>
            prev.map((f) => {
              const matches =
                f.fieldName === field.fieldName || f.id === field.id;
              if (matches) {
                const updatedVal =
                  String(f.value) === String(optionValue)
                    ? newOptionValue
                    : f.value;
                return {
                  ...f,
                  options: savedOptions,
                  MasterFormFieldOptions: savedOptions,
                  value: updatedVal,
                };
              }
              return f;
            }),
          );

          setMasterFieldCategories((prev) =>
            prev.map((cat) => ({
              ...cat,
              MasterFields: cat.MasterFields?.map((f) => {
                const matches =
                  f.FieldName === field.fieldName || f.Id === field.id;
                if (matches) {
                  return {
                    ...f,
                    MasterFormFieldOptions: savedOptions,
                    options: savedOptions,
                  };
                }
                return f;
              }),
            })),
          );
        } else {
          toast.error(response.message || "Failed to update option");
        }
      }
    } catch (error) {
      console.error("Error editing option:", error);
      toast.error("An error occurred while editing option");
    }
  };

  const handleDeleteDropdownOption = async (field, optionValue, label) => {
    if (!field) return;

    const isLeadSource = field.FieldTypeId === 13 || field.fieldTypeId === 13;

    try {
      if (isLeadSource) {
        const response = await deleteLeadSource(optionValue);
        if (response.success) {
          toast.success("Lead source deleted successfully");

          setDynamicOptions((prev) => {
            const fieldNameBase = (
              field.FieldName ||
              field.fieldName ||
              ""
            ).replace(/_\d+$/, "");
            const filterList = (list) =>
              list.filter((opt) => String(opt.value) !== String(optionValue));
            return {
              ...prev,
              [field.FieldName || field.fieldName]: filterList(
                prev[field.FieldName || field.fieldName] || [],
              ),
              [fieldNameBase]: filterList(prev[fieldNameBase] || []),
            };
          });

          // If the deleted option was currently selected, clear it
          setSelectedCustomFields((prev) =>
            prev.map((f) => {
              if (f.id === field.id) {
                return {
                  ...f,
                  value: String(f.value) === String(optionValue) ? "" : f.value,
                };
              }
              return f;
            }),
          );
        } else {
          toast.error(response.error || "Failed to delete option");
        }
      } else {
        const existingOptions =
          field.MasterFormFieldOptions || field.options || [];
        const updatedOptions = existingOptions
          .filter((opt) => {
            const optVal = opt.OptionValue || opt.optionValue || opt.value;
            return (
              String(optVal).trim().toLowerCase() !==
              String(optionValue).trim().toLowerCase()
            );
          })
          .map((opt, idx) => ({
            Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
            OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
            OptionValue: opt.OptionValue || opt.optionValue || opt.value,
            OptionOrder: idx + 1,
          }));

        const categoryId = getResolvedCategoryId(field);

        const payload = {
          id: field.MasterFieldId || field.id || field.Id,
          fieldName: field.FieldName || field.fieldName,
          fieldLabel: field.FieldLabel || field.label,
          fieldTypeId: field.FieldTypeId || field.fieldTypeId || 7,
          isMendatory: field.IsMendatory || field.isMendatory || false,
          placeholder: field.Placeholder || field.placeholder || field.label,
          isActive: true,
          defaultValue: field.DefaultValue || field.defaultValue || "",
          fieldOrder: field.FieldOrder || field.fieldOrder || 0,
          options: updatedOptions,
          isCommon: true,
          categoryId: categoryId,
          isDeleted: false,
        };

        const response = await upsertMasterField(payload);
        if (response.success) {
          toast.success("Option deleted successfully");

          const parsedData =
            typeof response.data?.data === "string"
              ? JSON.parse(response.data.data)
              : response.data?.data;
          const savedOptions =
            parsedData?.MasterFormFieldOptions ||
            parsedData?.options ||
            updatedOptions;

          setSelectedCustomFields((prev) =>
            prev.map((f) => {
              const matches =
                f.fieldName === field.fieldName || f.id === field.id;
              if (matches) {
                return {
                  ...f,
                  options: savedOptions,
                  MasterFormFieldOptions: savedOptions,
                  value: String(f.value) === String(optionValue) ? "" : f.value,
                };
              }
              return f;
            }),
          );

          setMasterFieldCategories((prev) =>
            prev.map((cat) => ({
              ...cat,
              MasterFields: cat.MasterFields?.map((f) => {
                const matches =
                  f.FieldName === field.fieldName || f.Id === field.id;
                if (matches) {
                  return {
                    ...f,
                    MasterFormFieldOptions: savedOptions,
                    options: savedOptions,
                  };
                }
                return f;
              }),
            })),
          );
        } else {
          toast.error(response.message || "Failed to delete option");
        }
      }
    } catch (error) {
      console.error("Error deleting option:", error);
      toast.error("An error occurred while deleting option");
    }
  };

  const handleCreateGroup = async (newGroupName) => {
    try {
      const payload = {
        groupId: null,
        GroupId: null,
        groupName: newGroupName,
        GroupName: newGroupName,
        companyId: getCompanyIdFromToken(),
        CompanyId: getCompanyIdFromToken(),
        userId: getUserIdFromToken(),
        UserId: getUserIdFromToken(),
      };
      const res = await createJobGroup(payload);
      if (res.success) {
        toast.success(res.message || "Job group created successfully");
        await fetchGroups();
        invalidateJobGroupsCache();
        if (res.data) {
          let createdData = res.data.data || res.data.Data || res.data;
          if (typeof createdData === "string") {
            try {
              createdData = JSON.parse(createdData);
            } catch (e) { }
          }
          const newId = createdData?.GroupId || createdData?.groupId;
          if (newId) {
            setJobGroup(newId);
          }
        }
      } else {
        console.error("Failed to create job group:", res.message);
        toast.error(res.message || "Failed to create job group");
      }
    } catch (err) {
      console.error("Error creating job group:", err);
      toast.error(err.message || "Error creating job group");
    }
  };

  const handleEditGroup = async (groupId, oldName, updatedName) => {
    try {
      const payload = {
        groupId: groupId,
        GroupId: groupId,
        groupName: updatedName,
        GroupName: updatedName,
        companyId: getCompanyIdFromToken(),
        CompanyId: getCompanyIdFromToken(),
        userId: getUserIdFromToken(),
        UserId: getUserIdFromToken(),
      };
      const res = await createJobGroup(payload);
      if (res.success) {
        toast.success(res.message || "Job group updated successfully");
        await fetchGroups();
        invalidateJobGroupsCache();
      } else {
        console.error("Failed to update job group:", res.message);
        toast.error(res.message || "Failed to update job group");
      }
    } catch (err) {
      console.error("Error updating job group:", err);
      toast.error(err.message || "Error updating job group");
    }
  };

  const handleDeleteGroup = async (groupId, label) => {
    try {
      const res = await deleteJobGroup(groupId);
      if (res.success) {
        toast.success(res.message || "Job group deleted successfully");
        await fetchGroups();
        invalidateJobGroupsCache();
        if (jobGroup === groupId) {
          setJobGroup("");
        }
      } else {
        console.error("Failed to delete job group:", res.message);
        toast.error(res.message || "Failed to delete job group");
      }
    } catch (err) {
      console.error("Error deleting job group:", err);
      toast.error(err.message || "Error deleting job group");
    }
  };

  // Pre-fill lead data logic
  useEffect(() => {
    // 1. Prefill from leadData (same as existing logic)
    if (leadData) {
      // Helper to parse leadinfo
      let leadInfoParsed = null;
      if (leadData.leadinfo) {
        try {
          leadInfoParsed =
            typeof leadData.leadinfo === "string"
              ? JSON.parse(leadData.leadinfo)
              : leadData.leadinfo;
        } catch (e) {
          // ignore
        }
      }

      // Helper to get contact info from LeadContact / LeadContacts
      let leadContact =
        prefillData?.LeadContact ||
        leadData.LeadContact ||
        leadData.LeadContacts;
      if (!leadContact && leadInfoParsed) {
        leadContact = leadInfoParsed.LeadContact || leadInfoParsed.LeadContacts;
      }
      if (typeof leadContact === "string" && leadContact) {
        try {
          leadContact = JSON.parse(leadContact);
        } catch (e) {
          // ignore
        }
      }
      const primaryContact =
        Array.isArray(leadContact) && leadContact.length > 0
          ? leadContact[0]
          : null;
      const contactName = primaryContact
        ? primaryContact.Fullname ||
        primaryContact.FullName ||
        primaryContact.fullName ||
        primaryContact.name
        : "";
      const contactPhone = primaryContact
        ? primaryContact.PhoneNumber || primaryContact.phoneNumber
        : "";
      const contactEmail = primaryContact
        ? primaryContact.Email || primaryContact.email
        : "";

      // Helper to get dynamic field values
      const getDynamicFieldValue = (fieldName) => {
        if (!leadData.DynamicFields) return null;
        try {
          const fields =
            typeof leadData.DynamicFields === "string"
              ? JSON.parse(leadData.DynamicFields)
              : leadData.DynamicFields;
          const field = fields.find(
            (f) =>
              f.FieldName === fieldName ||
              f.FieldName === fieldName.replace(/_/g, ""),
          );
          return field ? field.FieldValue : null;
        } catch (e) {
          return null;
        }
      };
      const dynAddress =
        getDynamicFieldValue("address") ||
        getDynamicFieldValue("street_address") ||
        getDynamicFieldValue("street_address_cont") ||
        getDynamicFieldValue("address_cont");
      const dynSuburb =
        getDynamicFieldValue("suburb") ||
        getDynamicFieldValue("city") ||
        getDynamicFieldValue("town") ||
        getDynamicFieldValue("suburb_cont") ||
        getDynamicFieldValue("city_cont");
      const dynState =
        getDynamicFieldValue("state") || getDynamicFieldValue("state_cont");
      const dynPostalCode =
        getDynamicFieldValue("postal_code") ||
        getDynamicFieldValue("postcode") ||
        getDynamicFieldValue("postalCode") ||
        getDynamicFieldValue("postCode") ||
        getDynamicFieldValue("postal_code_cont") ||
        getDynamicFieldValue("postcode_cont");

      const dynTitle =
        getDynamicFieldValue("title") ||
        getDynamicFieldValue("Title") ||
        getDynamicFieldValue("job_title");
      const name =
        leadData?.title ||
        leadData?.Title ||
        leadData?.name ||
        leadData?.Name ||
        dynTitle ||
        leadInfoParsed?.title ||
        leadInfoParsed?.Title ||
        "Unknown";
      const address =
        leadData.address ||
        dynAddress ||
        leadInfoParsed?.address ||
        leadInfoParsed?.Address ||
        "";
      const suburb =
        leadData.suburb ||
        leadData.city ||
        dynSuburb ||
        leadInfoParsed?.suburb ||
        leadInfoParsed?.suburbName ||
        leadInfoParsed?.city ||
        "";
      const state =
        leadData.state ||
        dynState ||
        leadInfoParsed?.state ||
        leadInfoParsed?.stateName ||
        "";
      const postalCode =
        leadData.postalCode ||
        leadData.postCode ||
        dynPostalCode ||
        leadInfoParsed?.postalCode ||
        leadInfoParsed?.postCode ||
        "";

      const clientName =
        leadData.clients || contactName || leadInfoParsed?.FullName || name;
      const clientPhone =
        leadData.phone ||
        leadData.clientPhone ||
        leadData.mobile ||
        contactPhone ||
        leadInfoParsed?.PhoneNumber ||
        leadInfoParsed?.phone ||
        "";
      const clientEmail =
        leadData.email ||
        leadData.clientEmail ||
        contactEmail ||
        leadInfoParsed?.Email ||
        leadInfoParsed?.email ||
        "";

      setTitle(name);
      setAddressVal(address);
      setSuburbVal(suburb);
      setStateVal(state);
      setPostalCodeVal(postalCode);

      // Extract Project Manager (Owner)
      let ownerName = "";
      if (leadData.assignedOwner) {
        ownerName = leadData.assignedOwner;
      } else if (leadData.owner) {
        ownerName = leadData.owner;
      } else if (leadData.Owners) {
        let ownersArr = leadData.Owners;
        if (typeof ownersArr === "string") {
          try {
            ownersArr = JSON.parse(ownersArr);
          } catch (e) {
            // ignore
          }
        }
        if (Array.isArray(ownersArr)) {
          ownerName = ownersArr
            .map((o) => o.OwnerName || o.name || o.FullName)
            .filter(Boolean)
            .join(", ");

          const mapped = ownersArr
            .map((o) => ({
              id: o.OwnerId || o.ownerId || o.UserId || o.userId || "",
              label: o.OwnerName || o.fullName || o.name || "",
              initial: (o.OwnerName || o.fullName || o.name || "U")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2),
            }))
            .filter((o) => o.id);
          setAssignedOwnersList(mapped);
        }
      }
      setProjectManagers([]);
      // Initialize clientsList
      setClientsList([
        {
          id:
            primaryContact?.ContactId ||
            primaryContact?.id ||
            primaryContact?.Id ||
            `temp-${Date.now()}`,
          name: clientName,
          fullName: clientName,
          FullName: clientName,
          email: clientEmail,
          Email: clientEmail,
          phone: clientPhone,
          phoneNumber: clientPhone,
          PhoneNumber: clientPhone,
          companyName: primaryContact?.companyName || primaryContact?.CompanyName || primaryContact?.ContactCompanyName || "",
          abnNumber: primaryContact?.ContactABNNumber || primaryContact?.ABNNumber || primaryContact?.ABN || primaryContact?.abn || primaryContact?.abnNumber || "",
          streetAddress: primaryContact?.streetAddress || primaryContact?.StreetAddress || primaryContact?.ContactStreetAddress || primaryContact?.address || primaryContact?.Address || address || "",
          suburb: primaryContact?.suburb || primaryContact?.Suburb || primaryContact?.ContactSuburb || primaryContact?.city || primaryContact?.City || suburb || "",
          city: primaryContact?.city || primaryContact?.City || primaryContact?.ContactCity || primaryContact?.suburb || primaryContact?.Suburb || suburb || "",
          state: primaryContact?.state || primaryContact?.State || primaryContact?.ContactState || state || "",
          postalCode: primaryContact?.postalCode || primaryContact?.PostalCode || primaryContact?.ContactPostalCode || primaryContact?.postcode || primaryContact?.Postcode || postalCode || "",
        },
      ]);

    }
    // 2. Override/merge from prefillData
    if (prefillData) {
      let data = prefillData.data || prefillData;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse prefillData:", e);
        }
      }
      if (data && (data.data || data.Data)) {
        data = data.data || data.Data;
      }
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // ignore
        }
      }


      if (data) {
        const resolvedTitle =
          data.Title ||
          data.title ||
          data.JobTitle ||
          data.jobTitle ||
          data.Name ||
          data.name;
        if (resolvedTitle) {
          setTitle(resolvedTitle);
        }
        if (data.prefix || data.Prefix || data.jobPrefix || data.JobPrefix) {
          setPrefix(
            data.prefix || data.Prefix || data.jobPrefix || data.JobPrefix,
          );
        }
        if (data.status || data.Status || data.jobStatus || data.JobStatus) {
          setJobStatus(
            data.status || data.Status || data.jobStatus || data.JobStatus,
          );
        }
        if (
          data.contractPrice ||
          data.ContractPrice ||
          data.price ||
          data.Price
        ) {
          setContractPrice(
            data.contractPrice ||
            data.ContractPrice ||
            data.price ||
            data.Price,
          );
        }
        if (data.jobType || data.JobType || data.type || data.Type) {
          setJobType(data.jobType || data.JobType || data.type || data.Type);
        }
        if (data.contractType || data.ContractType) {
          setContractType(data.contractType || data.ContractType);
        }
        if (
          data.address ||
          data.Address ||
          data.streetAddress ||
          data.StreetAddress
        ) {
          setAddressVal(
            data.address ||
            data.Address ||
            data.streetAddress ||
            data.StreetAddress,
          );
        }
        if (data.suburb || data.Suburb || data.city || data.City) {
          setSuburbVal(data.suburb || data.Suburb || data.city || data.City);
        }
        if (data.state || data.State) {
          setStateVal(data.state || data.State);
        }
        if (
          data.postalCode ||
          data.PostalCode ||
          data.postcode ||
          data.Postcode ||
          data.postCode ||
          data.PostCode
        ) {
          setPostalCodeVal(
            data.postalCode ||
            data.PostalCode ||
            data.postcode ||
            data.Postcode ||
            data.postCode ||
            data.PostCode,
          );
        }
        if (data.projectedStart || data.ProjectedStart) {
          setProjectedStart(data.projectedStart || data.ProjectedStart);
        }
        if (data.actualStart || data.ActualStart) {
          setActualStart(data.actualStart || data.ActualStart);
        }
        if (data.projectedCompletion || data.ProjectedCompletion) {
          setProjectedCompletion(
            data.projectedCompletion || data.ProjectedCompletion,
          );
        }
        if (data.actualCompletion || data.ActualCompletion) {
          setActualCompletion(data.actualCompletion || data.ActualCompletion);
        }
        if (data.updateActualDates !== undefined) {
          setUpdateActualDates(!!data.updateActualDates);
        }
        if (data.scheduleColor || data.ScheduleColor) {
          setScheduleColor(data.scheduleColor || data.ScheduleColor);
        }
        if (
          data.projectManager ||
          data.ProjectManager ||
          data.assignedOwner ||
          data.AssignedOwner
        ) {
          setProjectManagers([
            data.projectManager ||
            data.ProjectManager ||
            data.assignedOwner ||
            data.AssignedOwner,
          ]);
        }
        if (data.jobGroup || data.JobGroup) {
          setJobGroup(data.jobGroup || data.JobGroup);
        }
        if (data.squareMeters || data.SquareMeters) {
          setSquareMeters(data.squareMeters || data.SquareMeters);
        }
        if (data.permitNumber || data.PermitNumber) {
          setPermitNumber(data.permitNumber || data.PermitNumber);
        }
        if (data.fundedByLoan !== undefined) {
          setFundedByLoan(data.fundedByLoan ? "Yes" : "No");
        }
        if (data.internalNotes || data.InternalNotes) {
          setInternalNotes(data.internalNotes || data.InternalNotes);
        }
        if (data.vendorInstructions || data.VendorInstructions) {
          setVendorInstructions(
            data.vendorInstructions || data.VendorInstructions,
          );
        }
        // Check for clients list in prefillData
        const clients =
          data.clients || data.Clients || data.contacts || data.Contacts;
        if (Array.isArray(clients) && clients.length > 0) {
          const mappedClients = clients.map((c, idx) => ({
            id: c.id || c.ContactId || c.Id || `temp-${Date.now() + idx}`,
            name: c.name || c.Name || c.fullName || c.FullName || "",
            email: c.email || c.Email || "",
            phone: c.phone || c.Phone || c.phoneNumber || c.PhoneNumber || "",
            companyName: c.companyName || c.CompanyName || c.ContactCompanyName || "",
            abnNumber: c.ContactABNNumber || c.ABNNumber || c.ABN || "",
            streetAddress: c.streetAddress || c.StreetAddress || c.ContactStreetAddress || "",
            suburb: c.suburb || c.Suburb || c.ContactSuburb || "",
            city: c.city || c.City || c.ContactCity || "",
            state: c.state || c.State || c.ContactState || "",
            postalCode: c.postalCode || c.PostalCode || c.ContactPostalCode || c.postcode || c.Postcode || "",
          }));
          setClientsList(mappedClients);
        }

        // Check for internal users in prefillData
        const internalUsers =
          data.InternalUsers || data.internalUsers || data.users || data.Users;
        if (Array.isArray(internalUsers) && internalUsers.length > 0) {
          const mappedUsers = internalUsers.map((u, idx) => {
            const desigs = u.designations || u.Designations || [];
            // A person can hold more than one designation; default only when they have exactly one.
            const defaultDesignationId = desigs.length === 1
              ? (desigs[0].DesignationId || desigs[0].designationId)
              : null;
            return {
              id: u.UserId || u.id || idx,
              label: u.UserName || u.userName || u.label || u.name || "",
              designation: u.DesignationName || u.designationName || "",
              designations: desigs,
              designationIds: defaultDesignationId ? [defaultDesignationId] : [],
              initial: (u.UserName || u.userName || u.label || u.name || "U")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2),
            };
          });
          setInternalUsersList(mappedUsers);
          setAssignedOwnersList(mappedUsers);

          // Update Project Managers dropdown list dynamically from these users
          const names = mappedUsers.map((u) => u.label);
          setProjectManagersList(names);

          // Find the one where IsProjectManager is true
          const pm = internalUsers.find(
            (u) => u.IsProjectManager || u.isProjectManager,
          );
          setProjectManagers([]);
        }
      }
    }
  }, [leadData, prefillData]);

  const toggleWorkDay = (day) => {
    if (workDays.includes(day)) {
      setWorkDays(workDays.filter((d) => d !== day));
    } else {
      setWorkDays([...workDays, day]);
    }
  };

  const deleteClient = (id) => {
    setClientsList((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSelectContact = (contact) => {
    const name =
      contact.FullName || contact.fullName || contact.name || "Unknown";
    const email = contact.Email || contact.email || "";
    const phone =
      contact.PhoneNumber || contact.phoneNumber || contact.phone || "";
    const newClient = {
      id: contact.ContactId || contact.id || contact.Id || `temp-${Date.now()}`,
      name,
      fullName: name,
      FullName: name,
      email,
      Email: email,
      phone,
      phoneNumber: phone,
      PhoneNumber: phone,
      abnNumber: contact.ABNNumber || contact.abnNumber || contact.abn || contact.Abn || "",
      companyName: contact.CompanyName || contact.companyName || contact.company_name || "",
      streetAddress: contact.StreetAddress || contact.streetAddress || contact.address || contact.Address || "",
      suburb: contact.Suburb || contact.suburb || "",
      city: contact.City || contact.city || "",
      state: contact.State || contact.state || "",
      postalCode: contact.PostalCode || contact.postalCode || contact.postCode || contact.postcode || "",
    };
    setClientsList((prev) => {
      if (prev.some((c) => c.id === newClient.id)) return prev;
      return [...prev, newClient];
    });
    setShowSelectContact(false);
  };

  const handleAddInternalUser = async (tokens) => {
    if (!tokens || tokens.length === 0) return;

    const leadId =
      leadData?.id ||
      leadData?.leadId ||
      leadData?.LeadId ||
      prefillData?.leadId ||
      prefillData?.id ||
      prefillData?.LeadId;
    if (leadId) {
      try {
        const existingOwners = assignedOwnersList.map((u) => ({
          ownerId: String(u.id),
          isPrimary: false,
        }));

        const newOwners = tokens.map((t) => ({
          ownerId: String(t.id),
          isPrimary: false,
        }));

        const combinedOwners = [...existingOwners];
        newOwners.forEach((no) => {
          if (!combinedOwners.some((co) => co.ownerId === no.ownerId)) {
            combinedOwners.push(no);
          }
        });

        if (
          combinedOwners.length > 0 &&
          !combinedOwners.some((co) => co.isPrimary)
        ) {
          combinedOwners[0].isPrimary = true;
        }

        const res = await updateLeadOwner(leadId, combinedOwners);
        if (res.success) {
          toast.success("Lead owner updated successfully");
          const nextAssigned = [...assignedOwnersList];
          tokens.forEach((t) => {
            if (!nextAssigned.some((u) => String(u.id) === String(t.id))) {
              nextAssigned.push(t);
            }
          });
          setAssignedOwnersList(nextAssigned);
          const names = nextAssigned.map((u) => u.label);
          setProjectManagersList(names);
        } else {
          toast.error(res.error || "Failed to update lead owner");
        }
      } catch (err) {
        console.error("Error updating lead owner:", err);
        toast.error("Failed to update lead owner");
      }
    } else {
      const nextAssigned = [...assignedOwnersList];
      tokens.forEach((t) => {
        if (!nextAssigned.some((u) => String(u.id) === String(t.id))) {
          nextAssigned.push(t);
        }
      });
      setAssignedOwnersList(nextAssigned);
      const names = nextAssigned.map((u) => u.label);
      setProjectManagersList(names);
    }
  };

  const handleAddVendor = (tokens) => {
    const updatedList = [...vendorsList];
    tokens.forEach((t) => {
      if (!updatedList.some((v) => v.id === t.id)) {
        updatedList.push(t);
      }
    });
    setVendorsList(updatedList);
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!jobStatus || Number(jobStatus) === 0)
      newErrors.jobStatus = "Status is required";
    if (!jobType) newErrors.jobType = "Job type is required";
    if (!contractType) newErrors.contractType = "Contract type is required";
    if (!addressVal.trim()) newErrors.addressVal = "Street address is required";
    setErrors(newErrors);

    const firstError = Object.keys(newErrors)[0];
    if (firstError) {
      setTimeout(() => {
        const el = document.getElementById(`job-${firstError}-input`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 3) {
      if (validateStep3()) setStep(4);
    } else if (step === 4) {
      setStep(5);
    } else if (step === 5) {
      const hasUsersWithMultipleDesignations = assignedOwnersList.some(
        (u) =>
          Array.isArray(u.designations) &&
          u.designations.length > 1 &&
          (!Array.isArray(u.designationIds) || u.designationIds.length === 0)
      );
      if (hasUsersWithMultipleDesignations && !hasSkippedDesignationModal) {
        setShowDesignationConfirmModal(true);
        return;
      }
      setStep(6);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      onBack();
    } else {
      setStep(step - 1);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return; // hard guard against double-fire

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const leadId =
      leadData?.id ||
      leadData?.leadId ||
      leadData?.LeadId ||
      prefillData?.leadId ||
      prefillData?.id ||
      prefillData?.LeadId ||
      null;

    const formId =
      leadData?.formId ||
      leadData?.FormId ||
      prefillData?.formId ||
      prefillData?.FormId ||
      null;

    // Resolve project manager id from assignedOwnersList / internalUsersList / pmObjectsList by matching label
    const projectManagerId = projectManagers
      .map((pm) => {
        const pmEntry =
          assignedOwnersList.find((u) => u.label === pm) ||
          internalUsersList.find((u) => u.label === pm) ||
          pmObjectsList.find((u) => u.label === pm);
        return pmEntry ? String(pmEntry.id) : null;
      })
      .filter(Boolean);

    // Build workDays array for all 7 days with isActive flag
    const workDaysPayload = ALL_DAYS.map((day) => ({
      dayId: DAY_ID_MAP[day],
      isActive: workDays.includes(day),
    }));

    // Internal users, vendors, clients -> ids
    const internalUsersPayload = assignedOwnersList
      .filter((u) => u.id)
      .map((u) => ({
        userId: String(u.id),
        roleName: u.roleName || "",
        designationIds: Array.isArray(u.designationIds) ? u.designationIds : [],
      }));

    const vendorIds = selectedVendorsList
      .map((v) => v.id)
      .filter(Boolean)
      .map(String);

    const clientIds = clientsList
      .map((c) => c.id)
      .filter((id) => id && !String(id).startsWith("temp-")) // keep only real ids if they exist
      .map(String);
    // Fallback: if clients only have local Date.now() ids (number), they may
    // need to be created server-side first. Keeping as-is per current model;
    // adjust if backend expects different handling for new/local clients.

    // Custom fields -> dynamicFields
    const dynamicFields = [];
    for (const sf of selectedCustomFields) {
      let finalValue = "";
      if (sf.value !== undefined && sf.value !== null) {
        const isAttachmentField =
          sf.type === "attachments" ||
          sf.type === "file" ||
          sf.type === "image" ||
          sf.fieldTypeId === 10 ||
          sf.fieldTypeId === 16 ||
          sf.FieldTypeId === 10 ||
          sf.FieldTypeId === 16 ||
          (sf.fieldName || sf.FieldName || "").toLowerCase().includes("attachment");

        if (Array.isArray(sf.value) && (isAttachmentField || (sf.value.length > 0 && (sf.value[0] instanceof File || (sf.value[0] && typeof sf.value[0] === 'object' && !sf.value[0].label))))) {
          // Handle attachment uploads
          const filesArray = sf.value;
          const newFilesToUpload = filesArray.filter((item) => item instanceof File);
          const existingItems = filesArray.filter((item) => !(item instanceof File));

          let newlyUploadedDocs = [];
          if (newFilesToUpload.length > 0) {
            try {
              const uploadRes = await uploadAttachments(
                DocumentUploadFeature.ACTIVITY_ATTACHMENT,
                newFilesToUpload
              );
              if (uploadRes.success && uploadRes.data) {
                const nowIso = new Date().toISOString();
                newlyUploadedDocs = uploadRes.data.map((item) => ({
                  fileName: item.fileName,
                  filePath: item.filePath,
                  fileExtension: item.fileExtension,
                  fileSizeKB: item.fileSizeKB || 0,
                  uploadedAt: nowIso,
                }));
              } else {
                toast.error(uploadRes.message || "Failed to upload attachments for custom fields");
                setIsSubmitting(false);
                isSubmittingRef.current = false;
                return;
              }
            } catch (err) {
              console.error("Error uploading dynamic field attachments", err);
              toast.error("Failed to upload custom field attachments");
              setIsSubmitting(false);
              isSubmittingRef.current = false;
              return;
            }
          }

          const formattedExistingItems = existingItems.map((item) => {
            if (typeof item === "string") {
              const clean = item.trim();
              const name = clean.split("/").pop() || clean;
              const ext = name.includes(".") ? `.${name.split(".").pop().toLowerCase()}` : "";
              return {
                fileName: name,
                filePath: clean,
                fileExtension: ext,
                fileSizeKB: 0,
                uploadedAt: new Date().toISOString(),
              };
            }
            if (item && typeof item === "object") {
              const fPath = item.filePath || item.FilePath || item.path || item.Path || item.FileUrl || item.fileUrl || "";
              const fName = item.fileName || item.FileName || item.name || item.Name || (fPath ? fPath.split("/").pop() : "document");
              const fExt = item.fileExtension || item.FileExtension || (fName.includes(".") ? `.${fName.split(".").pop().toLowerCase()}` : "");
              return {
                ...item,
                fileName: fName,
                filePath: fPath,
                fileExtension: fExt,
                fileSizeKB: item.fileSizeKB || item.FileSizeKB || 0,
                uploadedAt: item.uploadedAt || item.UploadedAt || new Date().toISOString(),
              };
            }
            return item;
          });

          finalValue = JSON.stringify([...formattedExistingItems, ...newlyUploadedDocs]);
        } else if (Array.isArray(sf.value)) {
          finalValue = JSON.stringify(sf.value);
        } else {
          finalValue = String(sf.value);
        }
      }

      dynamicFields.push({
        masterFieldId: sf.masterFieldId || sf.MasterFieldId || sf.id || null,
        fieldName: sf.fieldName || sf.FieldName || "",
        fieldValue: finalValue,
      });
    }

    // Convert flatpickr "DD/MM/YYYY" output to ISO "YYYY-MM-DD" for the backend
    // (ASP.NET Core's DateTime? binder cannot parse "10/07/2026" — it requires ISO 8601)
    const parseFlatpickrDate = (str) => {
      if (!str) return null;
      // Already ISO-like (YYYY-MM-DD...) — leave as-is
      if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str;
      // DD/MM/YYYY → YYYY-MM-DD
      const parts = str.split("/");
      if (parts.length === 3) {
        const [d, m, y] = parts;
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      return null;
    };

    const payload = {
      companyId: getCompanyIdFromToken() || null,
      userId: getUserIdFromToken() || null,
      leadId: leadId,
      creationMode: 1, // scratch
      formId: formId,
      title: title || null,
      prefix: prefix || null,
      statusId: jobStatus && Number(jobStatus) > 0 ? Number(jobStatus) : null,
      jobTypeId: null,
      contractType: CONTRACT_TYPE_MAP[contractType] ?? 0,
      contractPrice: contractPrice ? parseFloat(contractPrice) : 0,
      streetAddress: addressVal || null,
      suburb: suburbVal || null,
      state: stateVal || null,
      postalCode: postalCodeVal || null,
      projectedStartDate: parseFlatpickrDate(projectedStart),
      actualStartDate: parseFlatpickrDate(actualStart),
      projectedCompletionDate: parseFlatpickrDate(projectedCompletion),
      actualCompletionDate: parseFlatpickrDate(actualCompletion),
      jobGroupId:
        jobGroup && jobGroup !== "-- Please Select --" ? jobGroup : null,
      projectManagerId: projectManagerId,
      squareMetres: squareMeters ? parseFloat(squareMeters) : 0,
      permitNumber: permitNumber || null,
      isLoanApplied: fundedByLoan === "Yes",
      scheduleColor: scheduleColor || null,
      workDays: workDaysPayload,
      internalUsers: internalUsersPayload,
      vendorIds: vendorIds,
      clientIds: clientIds,
      internalNotes: internalNotes || null,
      customerNotes: vendorInstructions || null,
      dynamicFields: dynamicFields,
    };
    try {
      const res = await convertLeadToJob(payload);
      if (res.success) {
        toast.success(res.message || "Successfully converted to job");
        onSubmit(payload, res.data);
      } else {
        toast.error(res.message || "Failed to convert lead to job");
      }
    } catch (err) {
      console.error("Error converting lead to job:", err);
      toast.error("An error occurred while converting to job");
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderStepper = () => {
    const stepsList = [
      { num: 3, label: "Job Info" },
      { num: 4, label: "Schedule" },
      { num: 5, label: "Clients & Personnel" },
      { num: 6, label: "Notes & Fields" },
    ];

    return (
      <div className="dark:border-dark-700 dark:bg-dark-800/30 shrink-0 border-b border-gray-100 bg-gray-50/50 px-8 py-3 select-none">
        <div className="relative mx-auto flex w-full max-w-3xl items-center justify-between">
          {/* Connector Line Background */}
          <div className="dark:bg-dark-700 absolute top-3 right-3 left-3 z-0 h-0.5 -translate-y-1/2 bg-gray-200" />

          {/* Active/Completed/Warranty Connector Line */}
          <div
            className="bg-success absolute top-3 left-3 z-0 h-0.5 -translate-y-1/2 transition-all duration-500 ease-in-out"
            style={{
              width: `calc((100% - 24px) * ${(Math.min(step, 6) - 3) / 3})`,
            }}
          />

          {stepsList.map((s, idx) => {
            const isActive = step === s.num;
            const isCompleted = step > s.num;
            return (
              <div
                key={s.num}
                className="group relative z-10 flex flex-col items-center"
              >
                {/* Circle */}
                <div
                  className={clsx(
                    "flex size-6 items-center justify-center rounded-full text-xs font-bold transition-all duration-300",
                    isActive &&
                    "bg-primary ring-primary-100 dark:ring-primary-900/30 scale-110 text-white ring-4",
                    isCompleted && "bg-success text-white",
                    !isActive &&
                    !isCompleted &&
                    "dark:bg-dark-800 dark:border-dark-700 border-2 border-gray-200 bg-white text-gray-400",
                  )}
                >
                  {isCompleted ? "✓" : idx + 1}
                </div>
                {/* Label */}
                <span
                  className={clsx(
                    "absolute top-8 text-center text-[11px] font-medium whitespace-nowrap transition-colors duration-300",
                    isActive
                      ? "text-primary dark:text-primary-400 font-semibold"
                      : isCompleted
                        ? "dark:text-dark-100 text-gray-800"
                        : "dark:text-dark-500 text-gray-500",
                  )}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
        {/* Spacer to account for absolute labels */}
        <div className="h-5" />
      </div>
    );
  };

  const getStepTitleAndDesc = () => {
    switch (step) {
      case 3:
        return { title: "Job Information & Address" };
      case 4:
        return { title: "Schedule & Info" };
      case 5:
        return { title: "Clients & Personnel" };
      case 6:
        return { title: "Notes & Custom Fields" };
      default:
        return { title: "Create Job" };
    }
  };

  const stepMeta = getStepTitleAndDesc();

  return (
    <div className="flex h-full flex-col overflow-hidden text-left">
      {/* Step Header */}
      <div className="dark:border-dark-600 flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 py-3">
        <div>
          <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
            {stepMeta.title}
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            onClick={onClose}
            className="dark:hover:text-dark-200 text-gray-400 transition-colors hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stepper Progress */}
      {renderStepper()}

      {/* Main Internally Scrollable Form Area */}
      <div className="flex-1 space-y-4 overflow-y-auto px-6 pt-4 pb-2 [&_.input-label]:text-[13px]">
        {/* ── STEP 3: Job Information ── */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="relative">
                <Input
                  id="job-title-input"
                  label="Title"
                  required
                  placeholder="Enter job title"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onFocus={() => {
                    if (jobTitleSuggestions.length > 0) setShowJobTitleSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setShowJobTitleSuggestions(false), 150)}
                  error={errors.title}
                  className="h-10"
                />
                {showJobTitleSuggestions && (isSearchingJobTitle || jobTitleSuggestions.length > 0) && (
                  <div
                    className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-dark-600"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {isSearchingJobTitle ? (
                      <div className="flex items-center gap-2.5 px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                        <svg className="animate-spin h-4 w-4 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        <span>Checking for existing jobs…</span>
                      </div>
                    ) : (
                      <>
                        <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                          Possible existing job{jobTitleSuggestions.length !== 1 ? "s" : ""} — check before creating a new one
                        </div>
                        {jobTitleSuggestions.map((job) => {
                          const subtitleParts = [job.StreetAddress, job.Suburb, job.City, job.State].filter(Boolean);
                          return (
                            <div
                              key={job.JobId}
                              className="flex items-center justify-between gap-2 p-3 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm text-gray-800 dark:text-white truncate">{job.Title}</div>
                                {subtitleParts.length > 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                    {subtitleParts.join(", ")}
                                  </div>
                                )}
                              </div>
                              {/* The viewer's own job-visibility permission is checked here (GetJobById)
                                  before opening anything, so a denied viewer sees a toast and this modal
                                  stays open, instead of being bounced to /jobs/:jobId and redirected back. */}
                              <button
                                type="button"
                                disabled={checkingViewJobId === job.JobId}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleViewExistingJob(job)}
                                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-dark-500 text-xs font-semibold text-gray-700 dark:text-dark-100 hover:bg-white dark:hover:bg-dark-600 hover:border-primary-400 transition-colors disabled:opacity-60 disabled:cursor-wait"
                              >
                                <EyeIcon className="size-3.5 text-teal-500" />
                                {checkingViewJobId === job.JobId ? "Checking…" : "View"}
                              </button>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
              <Input
                label="Contract price"
                placeholder="0.00"
                type="number"
                prefix="$"
                value={contractPrice}
                onChange={(e) => setContractPrice(e.target.value)}
                className="h-10"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div
                id="job-jobStatus-input"
                onClick={() => handleFetchStatuses(false)}
                onFocus={() => handleFetchStatuses(false)}
                className="w-full"
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-dark-300 flex items-center gap-1">
                    <span>Status</span>
                    <span className="text-error font-bold">*</span>
                  </label>
                  {canManageJobStatus() && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowManageStatuses(true);
                      }}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-dark-500 bg-white dark:bg-dark-700 px-2.5 py-1.5 text-xs font-semibold text-gray-600 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-primary/40 hover:text-primary dark:hover:text-primary-400 active:scale-95 transition-all shadow-sm"
                    >
                      <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                      Manage
                    </button>
                  )}
                </div>
                <MultiSelectCheckboxDropdown
                  label={null}
                  required={true}
                  options={
                    jobStatuses.length > 0
                      ? jobStatuses.map(s => ({ ...s, label: s.alias || s.label }))
                      : jobStatus
                        ? [
                          {
                            label: jobStatus === 2 ? "Ongoing" : "Loading...",
                            value: jobStatus,
                          },
                        ]
                        : []
                  }
                  value={jobStatus === 0 ? "" : jobStatus}
                  onChange={(val) => {
                    setJobStatus(val ? Number(val) : 0);
                    if (val) {
                      setErrors((prev) => ({ ...prev, jobStatus: null }));
                    }
                  }}
                  isMulti={false}
                  placeholder="Select..."
                  error={errors.jobStatus}
                />
              </div>
            </div>

            {/* <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <div className="flex items-center justify-between px-1">
                  <label className="input-label flex items-center gap-1">
                    <span className="input-label">Type</span>
                    <span className="text-error">*</span>
                  </label>
                  
                </div>
                <ThemeSelect
                  data={["Custom New Home", "Renovation"]}
                  value={jobType}
                  onChange={setJobType}
                  error={errors.jobType}
                />
              </div>
            </div> */}

            <div className="space-y-1">
              <label
                id="job-contractType-input"
                className="input-label flex items-center gap-1"
              >
                <span className="input-label">Contract type</span>
                <span className="text-error">*</span>
              </label>
              {errors.contractType && (
                <p className="text-error text-xs font-medium">
                  {errors.contractType}
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div
                  onClick={() => setContractType("Fixed price")}
                  className={clsx(
                    "cursor-pointer rounded-xl border-2 p-2.5 transition-all",
                    contractType === "Fixed price"
                      ? "border-primary bg-primary-50/10 dark:bg-primary-500/5 dark:border-primary-400"
                      : "dark:border-dark-700 border-slate-100 hover:border-slate-200",
                  )}
                >
                  <Radio
                    checked={contractType === "Fixed price"}
                    onChange={() => { }}
                    label={
                      <div className="ml-1 flex flex-col">
                        <span className="text-sm font-bold text-slate-900 dark:text-white">
                          Fixed price
                        </span>
                        <span className="dark:text-dark-400 mt-0.5 text-[10px] leading-tight text-slate-500">
                          You will set the contract price for the client
                        </span>
                      </div>
                    }
                  />
                </div>
                <div
                  onClick={() => setContractType("Open book")}
                  className={clsx(
                    "cursor-pointer rounded-xl border-2 p-2.5 transition-all",
                    contractType === "Open book"
                      ? "border-primary bg-primary-50/10 dark:bg-primary-500/5 dark:border-primary-400"
                      : "dark:border-dark-700 border-slate-100 hover:border-slate-200",
                  )}
                >
                  <Radio
                    checked={contractType === "Open book"}
                    onChange={() => { }}
                    label={
                      <div className="ml-1 flex flex-col">
                        <span className="text-sm font-bold text-slate-900 dark:text-white">
                          Open book
                        </span>
                        <span className="dark:text-dark-400 mt-0.5 text-[10px] leading-tight text-slate-500">
                          Price = projected + markup
                        </span>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
            <div className="dark:border-dark-700 mt-6 space-y-1 border-t border-gray-100 pt-6">
              <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-white">
                Job Location
              </h3>
              <div className="space-y-6">
                <Input
                  id="job-addressVal-input"
                  label="Street address"
                  required
                  placeholder="Enter street address"
                  value={addressVal}
                  onChange={(e) => setAddressVal(e.target.value)}
                  error={errors.addressVal}
                  className="h-11"
                />
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <label className="input-label flex items-center gap-1">
                      <span className="input-label">Suburb</span>
                    </label>
                    <CustomCreatableDropdown
                      options={suburbOptions}
                      value={
                        suburbOptions.find((o) => o.label === suburbVal) ||
                        (suburbVal
                          ? { value: suburbVal, label: suburbVal }
                          : null)
                      }
                      onChange={(newValue) => {
                        const label = newValue ? newValue.label : "";
                        setSuburbVal(label);
                        setStateVal("");
                        setPostalCodeVal("");
                        lookupSuburbRecommendation(label);
                      }}
                      onCreateOption={handleCreateSuburb}
                      onEditOption={handleEditSuburb}
                      onDeleteOption={handleDeleteSuburb}
                      placeholder="Select or type..."
                      isLoading={isLoadingSuburbs}
                      menuHeader="Select Suburb"
                      addNewText="+ Add New Suburb"
                      deleteConfirmationMessage="Are you sure you want to delete this suburb?"
                    />
                  </div>
                  <Input
                    label="State"
                    placeholder="State"
                    value={stateVal}
                    onChange={(e) => setStateVal(e.target.value)}
                    className="h-11"
                  />
                  <Input
                    label="Postal code"
                    placeholder="Postal code"
                    value={postalCodeVal}
                    onChange={(e) => setPostalCodeVal(e.target.value)}
                    className="h-11"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: Schedule & Additional Info ── */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <DatePicker
                label="Projected start"
                value={projectedStart}
                onChange={(dates, dateStr) => setProjectedStart(dateStr)}
                placeholder="Select date"
              />
              <DatePicker
                label="Actual start"
                value={actualStart}
                onChange={(dates, dateStr) => setActualStart(dateStr)}
                placeholder="Select date"
              />
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <DatePicker
                label="Projected completion"
                value={projectedCompletion}
                onChange={(dates, dateStr) => setProjectedCompletion(dateStr)}
                placeholder="Select date"
              />
              <DatePicker
                label="Actual completion"
                value={actualCompletion}
                onChange={(dates, dateStr) => setActualCompletion(dateStr)}
                placeholder="Select date"
              />
            </div>
            <div className="pl-1"></div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="input-root">
                <label className="input-label flex items-center gap-1">
                  <span className="input-label">Project managers</span>
                </label>
                <div className="mt-1.5 w-full">
                  <ReactSelect
                    isMulti
                    options={pmObjectsList.map((pmObj) => ({
                      value: pmObj.label,
                      label: pmObj.label,
                      designation: pmObj.designation,
                      designations: pmObj.designations,
                    }))}
                    value={projectManagers.map((pm) => {
                      const pmObj = pmObjectsList.find(obj => obj.label === pm);
                      return {
                        value: pm,
                        label: pm,
                        designation: pmObj ? pmObj.designation : "",
                        designations: pmObj ? pmObj.designations : [],
                      };
                    })}
                    onChange={(selected) => {
                      const newPMs = selected ? selected.map((s) => s.value) : [];
                      const prevPMs = projectManagers;
                      setProjectManagers(newPMs);
                      prevProjectManagersRef.current = newPMs;
                      syncPMsToAssignedOwners(newPMs, prevPMs);
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: "42px",
                        borderRadius: "0.75rem",
                        borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
                        boxShadow: state.isFocused
                          ? "0 0 0 1px #6366f1"
                          : "none",
                        "&:hover": {
                          borderColor: state.isFocused ? "#6366f1" : "#cbd5e1",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#EEF2FF",
                        borderRadius: "0.5rem",
                        padding: "1px 4px",
                        margin: "2px 4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#4F46E5",
                        fontWeight: "700",
                        fontSize: "0.75rem",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#4F46E5",
                        cursor: "pointer",
                        ":hover": {
                          backgroundColor: "#E0E7FF",
                          color: "#3730A3",
                          borderRadius: "0.375rem",
                        },
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    className="react-select-container w-full text-sm"
                    classNamePrefix="react-select"
                    formatOptionLabel={(option, { context }) => {
                      if (context === "value") {
                        return <span>{option.label}</span>;
                      }

                      const designationsList = Array.isArray(option.designations) && option.designations.length > 0
                        ? option.designations.map(d => d.DesignationName || d.designationName)
                        : option.designation ? [option.designation] : [];

                      const maxVisible = 2;
                      const visibleBadges = designationsList.slice(0, maxVisible);
                      const extraBadges = designationsList.slice(maxVisible);

                      return (
                        <div className="flex items-center justify-between min-w-0">
                          <span className="truncate pr-2">{option.label}</span>
                          {designationsList.length > 0 && (
                            <div className="ml-2 flex shrink-0 items-center justify-end gap-1 flex-wrap">
                              {visibleBadges.map((badge, idx) => (
                                <span
                                  key={idx}
                                  title={badge}
                                  className={`shrink-0 max-w-full inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${badge === "Project Manager"
                                      ? "bg-primary text-white"
                                      : "bg-[#f1f5f9] text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                                    }`}
                                >
                                  <span className="truncate">{badge}</span>
                                </span>
                              ))}
                              {extraBadges.length > 0 && (
                                <div className="relative inline-flex items-center">
                                  <span
                                    data-tooltip-id={`badge-tooltip-${option.label.replace(/\s+/g, '-')}`}
                                    data-tooltip-html={extraBadges.join("<br/>")}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    className="inline-flex items-center rounded-full bg-[#f1f5f9] px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                                  >
                                    +{extraBadges.length}
                                  </span>
                                  <ReactTooltip
                                    id={`badge-tooltip-${option.label.replace(/\s+/g, '-')}`}
                                    place="right"
                                    className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2 !py-1 !rounded-md !shadow-lg"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                    filterOption={(candidate, input) => {
                      if (!input) return true;
                      const searchStr = input.toLowerCase();
                      const designations = Array.isArray(candidate.data?.designations) && candidate.data.designations.length > 0
                        ? candidate.data.designations.map(d => (d.DesignationName || d.designationName || "").toLowerCase())
                        : [(candidate.data?.designation || "").toLowerCase()];
                      return (
                        (candidate.label || "").toLowerCase().includes(searchStr) ||
                        designations.some(d => d.includes(searchStr))
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="input-label flex items-center gap-1">
                <span className="input-label">Work days</span>
                <span className="text-error">*</span>
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ].map((day) => {
                  const active = workDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleWorkDay(day)}
                      className={clsx(
                        "rounded-lg border-2 px-3 py-1 text-[10px] font-bold transition-all",
                        active
                          ? "bg-primary border-primary text-white"
                          : "dark:bg-dark-700 dark:text-dark-400 dark:border-dark-600 border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200",
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="input-label flex items-center gap-1">
                  <span className="input-label">Job group</span>
                </label>
                <CustomCreatableDropdown
                  options={jobGroups}
                  value={jobGroups.find((g) => g.value === jobGroup) || null}
                  onChange={(newValue) => {
                    setJobGroup(newValue ? newValue.value : "");
                  }}
                  onCreateOption={handleCreateGroup}
                  onEditOption={handleEditGroup}
                  onDeleteOption={handleDeleteGroup}
                  placeholder="Select or type to add job group..."
                  isLoading={isLoadingGroups}
                  menuHeader="Select Job Group"
                  addNewText="+ Add New Group"
                  deleteConfirmationMessage="Are you sure you want to delete the job group?"
                />
              </div>
              <Input
                label="Square meters"
                type="number"
                placeholder="0"
                value={squareMeters}
                onChange={(e) => setSquareMeters(e.target.value)}
                className="h-10"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                label="Permit number"
                placeholder="Enter permit number"
                value={permitNumber}
                onChange={(e) => setPermitNumber(e.target.value)}
                className="h-10"
              />
              <div className="space-y-3">
                <label className="input-label flex items-center gap-1">
                  <span className="input-label">
                    Funded by construction loan
                  </span>
                </label>
                <div className="mt-2 flex gap-6 pl-1.5">
                  <Radio
                    checked={fundedByLoan === "Yes"}
                    onChange={() => setFundedByLoan("Yes")}
                    label={
                      <span className="dark:text-dark-200 text-xs font-bold text-slate-700">
                        Yes
                      </span>
                    }
                  />
                  <Radio
                    checked={fundedByLoan === "No"}
                    onChange={() => setFundedByLoan("No")}
                    label={
                      <span className="dark:text-dark-200 text-xs font-bold text-slate-700">
                        No
                      </span>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 5: Clients & Personnel ── */}
        {step === 5 && (
          <div className="space-y-10">
            {/* Clients Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlusIcon className="text-primary h-5 w-5" />
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    Clients
                  </h3>
                </div>
                <div className="flex gap-2">
                  <Menu as="div" className="relative inline-block text-left">
                    <MenuButton className="dark:bg-dark-800 dark:border-dark-700 dark:hover:bg-dark-700 flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:text-white">
                      <UserPlusIcon className="h-3.5 w-3.5" />
                      Add Clients
                    </MenuButton>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg border border-gray-200 focus:outline-none dark:bg-dark-800 dark:border-dark-600">
                        <MenuItem>
                          {({ active }) => (
                            <button
                              onClick={() => setShowSelectContact(true)}
                              className={clsx(
                                active ? "bg-gray-100 dark:bg-dark-700" : "",
                                "flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
                              )}
                            >
                              <UserIcon className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
                              Select Existing
                            </button>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ active }) => (
                            <button
                              onClick={() => setShowAddContact(true)}
                              className={clsx(
                                active ? "bg-gray-100 dark:bg-dark-700" : "",
                                "flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
                              )}
                            >
                              <PlusIcon className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
                              Add New Client
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              </div>

              {/* Selected Clients in Card Form (Matching ClientsTab style) */}
              {clientsList.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {clientsList.map((client) => {
                    const initials =
                      client.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2) || "C";
                    return (
                      <div
                        key={client.id}
                        className="dark:border-dark-600 dark:bg-dark-800 relative flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-white p-3 text-left"
                      >
                        {/* Avatar + name + role + delete */}
                        <div className="flex items-center gap-2.5">
                          <div className="bg-primary-50 dark:bg-primary-900/30 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                            <span className="text-primary dark:text-primary-400 text-xs font-bold">
                              {initials}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="dark:text-dark-100 text-xs leading-tight font-bold break-words text-gray-800">
                              {client.name}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingContact({
                                  ...client,
                                  id: client.id || client.ContactId || client.Id,
                                  ContactId: client.id || client.ContactId || client.Id,
                                  fullName: client.name || client.fullName || client.FullName,
                                  FullName: client.name || client.fullName || client.FullName,
                                  phoneNumber: client.phone || client.phoneNumber || client.PhoneNumber,
                                  PhoneNumber: client.phone || client.phoneNumber || client.PhoneNumber,
                                  email: client.email || client.Email,
                                  Email: client.email || client.Email,
                                  companyName: client.companyName || client.CompanyName,
                                  CompanyName: client.companyName || client.CompanyName,
                                  abnNumber: client.abnNumber || client.ABNNumber || client.abn || client.Abn,
                                  ABNNumber: client.abnNumber || client.ABNNumber || client.abn || client.Abn,
                                  streetAddress: client.streetAddress || client.StreetAddress,
                                  StreetAddress: client.streetAddress || client.StreetAddress,
                                  suburb: client.suburb || client.Suburb,
                                  Suburb: client.suburb || client.Suburb,
                                  postalCode: client.postalCode || client.PostalCode || client.postcode,
                                  PostalCode: client.postalCode || client.PostalCode || client.postcode,
                                  state: client.state || client.State,
                                  State: client.state || client.State,
                                });
                                setShowEditContactModal(true);
                              }}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-700 dark:hover:text-dark-200"
                              title="Edit Client"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteClient(client.id)}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/10"
                              title="Delete Client"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Contact info */}
                        <div className="dark:border-dark-700 space-y-1 border-t border-gray-50 pt-2">
                          <div className="dark:text-dark-400 flex items-start gap-1.5 text-xs text-gray-500">
                            <PhoneIcon className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                            <span className="min-w-0 flex-1 break-words">
                              {client.phone || "—"}
                            </span>
                          </div>
                          <div className="dark:text-dark-400 flex items-start gap-1.5 text-xs text-gray-500">
                            <EnvelopeIcon className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                            <span className="min-w-0 flex-1 break-words">
                              {client.email || "—"}
                            </span>
                          </div>
                        </div>

                        {/* Optional company/ABN details */}
                        {(client.companyName || client.abnNumber) && (
                          <div className="dark:border-dark-700 space-y-1 border-t border-gray-50 pt-2">
                            {client.companyName && (
                              <div className="dark:text-dark-400 flex items-start gap-1 text-[10px] text-gray-500">
                                <BriefcaseIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                <span className="min-w-0 flex-1 break-words">
                                  {client.companyName}
                                </span>
                              </div>
                            )}
                            {client.abnNumber && (
                              <div className="dark:text-dark-400 flex items-start gap-1 text-[10px] text-gray-500">
                                <span className="mt-0.5 shrink-0 text-[9px] font-medium text-slate-400">
                                  ABN:
                                </span>
                                <span className="min-w-0 flex-1 break-words">
                                  {client.abnNumber}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="dark:bg-dark-800/50 dark:border-dark-700 rounded-xl border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-400 italic">
                  No clients selected yet. Click &quot;Select Existing&quot; or
                  &quot;New Client&quot; to add.
                </div>
              )}
            </div>

            {/* Personnel Access */}
            <div className="dark:border-dark-700 grid grid-cols-1 gap-8 border-t border-gray-100 pt-6 md:grid-cols-2">
              {/* Internal Users Dropdown */}
              <div className="space-y-4 text-left">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-emerald-50 p-2 text-emerald-500 dark:bg-emerald-500/10">
                    <UserPlusIcon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Internal User
                  </h3>
                </div>
                <div className="input-root">
                  <MultiSelectCheckboxDropdown
                    placeholder="Search users..."
                    searchPlaceholder="Search users..."
                    dropdownHeader={
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hideWorkerBoys}
                          onChange={(e) => setHideWorkerBoys(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Hide Worker/Boys
                      </label>
                    }
                    options={internalUsersList.map((u) => {
                      const roleText = u.designation || u.roleName || "Internal User";
                      const rolesList = Array.isArray(u.designations) && u.designations.length > 0
                        ? u.designations.map(d => d.DesignationName || d.designationName)
                        : (typeof roleText === 'string' ? roleText.split(',').map(s => s.trim()).filter(Boolean) : [roleText]);

                      const maxVisible = 1;
                      const visibleRoles = rolesList.slice(0, maxVisible);
                      const extraRoles = rolesList.slice(maxVisible);

                      return {
                        value: u.id,
                        label: u.label,
                        actions: (
                          <div className="pointer-events-none flex select-none items-center justify-end gap-1">
                            {visibleRoles.map((role, idx) => (
                              <span key={idx} className="dark:bg-dark-700 dark:text-dark-300 dark:border-dark-600 shrink-0 inline-flex items-center rounded-full border border-slate-200/60 bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500 max-w-[120px] truncate">
                                {role}
                              </span>
                            ))}
                            {extraRoles.length > 0 && (
                              <div className="relative inline-flex items-center pointer-events-auto">
                                <span
                                  data-tooltip-id={`badge-tooltip-internal-${u.id}`}
                                  data-tooltip-html={extraRoles.join("<br/>")}
                                  className="dark:bg-dark-700 dark:text-dark-300 dark:border-dark-600 shrink-0 inline-flex items-center rounded-full border border-slate-200/60 bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500 cursor-pointer hover:bg-slate-200 dark:hover:bg-dark-600 transition-colors"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                >
                                  +{extraRoles.length}
                                </span>
                                <ReactTooltip
                                  id={`badge-tooltip-internal-${u.id}`}
                                  place="right"
                                  className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2 !py-1 !rounded-md !shadow-lg"
                                />
                              </div>
                            )}
                          </div>
                        ),
                        ...u,
                        designation: null,
                        searchString: rolesList.join(" "),
                      };
                    })}
                    value={assignedOwnersList.map((u) => u.id)}
                    onChange={(selectedValues) => {
                      const selectedIds = Array.isArray(selectedValues)
                        ? selectedValues
                        : selectedValues
                          ? [selectedValues]
                          : [];
                      const newList = selectedIds
                        .map((id) =>
                          internalUsersList.find(
                            (u) => String(u.id) === String(id),
                          ),
                        )
                        .filter(Boolean)
                        .map((u) => {
                          // Preserve the designation(s) already picked for this person in a prior
                          // edit; otherwise default to their own designation when they only have
                          // one, leaving it unset (pick via the labels) when they have several —
                          // a person may hold more than one designation on this job at once.
                          const existing = assignedOwnersList.find((o) => String(o.id) === String(u.id));
                          const desigs = u.designations || [];
                          const defaultDesignationId = desigs.length === 1
                            ? (desigs[0].DesignationId || desigs[0].designationId)
                            : null;
                          const isPM = projectManagers.includes(u.label);
                          const pmDesig = desigs.find((d) =>
                            /project\s*manager/i.test(d.DesignationName || d.designationName || "")
                          );
                          const pmDesigId = pmDesig ? (pmDesig.DesignationId || pmDesig.designationId) : null;
                          let initialDesigIds = existing?.designationIds ?? (defaultDesignationId ? [defaultDesignationId] : []);
                          if (isPM && pmDesigId && !initialDesigIds.some((id) => String(id) === String(pmDesigId))) {
                            initialDesigIds = [...initialDesigIds, pmDesigId];
                          }

                          return {
                            id: u.id,
                            label: u.label,
                            designation: u.designationName || u.designation,
                            designations: desigs,
                            designationIds: initialDesigIds,
                            initial: u.initial,
                            roleName: u.roleName,
                          };
                        });

                      if (newList.length > assignedOwnersList.length) {
                        const addedItems = newList.filter(
                          (n) => !assignedOwnersList.some((o) => o.id === n.id),
                        );
                        if (addedItems.length > 0) {
                          toast.success(
                            `${addedItems[0].label} added successfully`,
                          );
                          setTimeout(() => {
                            internalUsersEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "nearest",
                            });
                          }, 100);
                        }
                      }

                      setAssignedOwnersList(newList);
                      setProjectManagersList(newList.map((u) => u.label));
                      const remainingLabels = newList.map((u) => u.label);
                      setProjectManagers((prev) => prev.filter((p) => remainingLabels.includes(p)));
                    }}
                  />
                </div>

                {/* Internal Users Cards Grid */}
                {assignedOwnersList.length > 0 && (
                  <div className="mt-3.5 space-y-3">
                    {assignedOwnersList.map((user) => {
                      const initials =
                        user.label
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase() || "U";
                      return (
                        <div
                          key={user.id}
                          className="dark:border-dark-600 dark:bg-dark-800 relative flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition-colors hover:border-gray-300 dark:hover:border-dark-500"
                        >
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30">
                            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                              {initials}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="dark:text-dark-100 text-xs font-bold leading-tight text-gray-800">
                                {user.label}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  const newList = assignedOwnersList.filter(
                                    (u) => u.id !== user.id,
                                  );
                                  setAssignedOwnersList(newList);
                                  setProjectManagersList(
                                    newList.map((u) => u.label),
                                  );
                                  setProjectManagers((prev) =>
                                    prev.filter((p) => p !== user.label)
                                  );
                                }}
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                                title="Remove user"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {Array.isArray(user.designations) && user.designations.length > 0 ? (
                                <>
                                  {user.designations.length > 1 && (
                                    <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50 shrink-0">
                                      Choose:
                                    </span>
                                  )}
                                  {user.designations.map((d) => {
                                    const id = d.DesignationId || d.designationId;
                                    const name = d.DesignationName || d.designationName;
                                    const selectedIds = Array.isArray(user.designationIds) ? user.designationIds : [];
                                    const isChosen = selectedIds.some((sid) => String(sid) === String(id));
                                    const isPMForJob = projectManagers.includes(user.label);
                                    const isPMDesignation = /project\s*manager/i.test(name || "");
                                    const isLocked = isPMForJob && isPMDesignation;

                                    return (
                                      <button
                                        key={id}
                                        type="button"
                                        onClick={() => {
                                          if (isLocked) {
                                            toast.info(`${user.label} is assigned as Project Manager for this job`);
                                            return;
                                          }
                                          const newList = assignedOwnersList.map((u) => {
                                            if (u.id !== user.id) return u;
                                            const current = Array.isArray(u.designationIds) ? u.designationIds : [];
                                            const next = current.some((cid) => String(cid) === String(id))
                                              ? current.filter((cid) => String(cid) !== String(id))
                                              : [...current, id];
                                            return { ...u, designationIds: next };
                                          });
                                          setAssignedOwnersList(newList);
                                        }}
                                        title={
                                          isLocked
                                            ? "Assigned as Project Manager on this job (cannot be unselected)"
                                            : "Click to toggle this as a designation they act as on this job"
                                        }
                                        className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 transition-all border ${isChosen
                                          ? "bg-primary-600 border-primary-600 text-white shadow-xs"
                                          : "bg-gray-100 dark:bg-dark-700 border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600"
                                          } ${isLocked ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`}
                                      >
                                        {isChosen && <CheckIcon className="w-3 h-3 shrink-0" strokeWidth={2.5} />}
                                        <span>{name}</span>
                                      </button>
                                    );
                                  })}
                                </>
                              ) : (
                                <span className="text-[11px] text-gray-400 dark:text-dark-400">
                                  {user.designation || user.roleName || "Internal User"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={internalUsersEndRef} />
                  </div>
                )}
              </div>

              {/* Contractors Dropdown */}
              <div className="space-y-4 text-left">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-violet-50 p-2 text-violet-500 dark:bg-violet-500/10">
                    <BuildingOfficeIcon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Contractors
                  </h3>
                </div>
                <div className="input-root">
                  <MultiSelectCheckboxDropdown
                    placeholder="Search contractors..."
                    searchPlaceholder="Search contractors..."
                    options={vendorsList.map((v) => {
                      const companyName = v.externalCompanyName || v.companyName;
                      const designations = Array.isArray(v.designations) && v.designations.length > 0
                        ? v.designations
                        : (v.designation ? [v.designation] : []);
                      const designationLabels = designations
                        .map(d => (typeof d === 'string' ? d : (d?.DesignationName || d?.designationName || '')))
                        .filter(Boolean);

                      return {
                        value: v.id,
                        label: v.label,
                        // Company renders as a subtitle under the contractor's name; designations use
                        // MultiSelectCheckboxDropdown's built-in first-pill + "+N" (hover for the rest)
                        // rendering — same mechanism other option lists in this dropdown already get,
                        // rather than the bespoke badge/tooltip markup this used to hand-roll here.
                        subtitle: companyName || null,
                        ...v,
                        designation: designations,
                        searchString: [companyName, ...designationLabels].filter(Boolean).join(" "),
                      };
                    })}
                    value={selectedVendorsList.map((v) => v.id)}
                    onChange={(selectedValues) => {
                      const selectedIds = Array.isArray(selectedValues)
                        ? selectedValues
                        : selectedValues
                          ? [selectedValues]
                          : [];
                      const newList = selectedIds
                        .map((id) =>
                          vendorsList.find((v) => String(v.id) === String(id)),
                        )
                        .filter(Boolean)
                        .map((o) => ({
                          id: o.id,
                          label: o.label,
                          initial: o.initial,
                          externalCompanyName: o.externalCompanyName || "",
                          designation: o.designation || "",
                          designations: o.designations || [],
                          tradeTypes: o.tradeTypes || [],
                        }));

                      if (newList.length > selectedVendorsList.length) {
                        const addedItems = newList.filter(
                          (n) =>
                            !selectedVendorsList.some((o) => o.id === n.id),
                        );
                        if (addedItems.length > 0) {
                          toast.success(
                            `${addedItems[0].label} added successfully`,
                          );
                          setTimeout(() => {
                            contractorsEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "nearest",
                            });
                          }, 100);
                        }
                      }

                      setSelectedVendorsList(newList);
                    }}
                  />
                </div>

                {/* Contractors Cards Grid */}
                {selectedVendorsList.length > 0 && (
                  <div className="mt-3.5 space-y-3">
                    {selectedVendorsList.map((vendor) => {
                      const initials =
                        vendor.label
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase() || "C";
                      return (
                        <div
                          key={vendor.id}
                          className="dark:border-dark-600 dark:bg-dark-800 relative flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition-colors hover:border-gray-300 dark:hover:border-dark-500"
                        >
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-50 dark:bg-violet-950/30">
                            <span className="text-[11px] font-bold text-violet-600 dark:text-violet-400">
                              {initials}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="dark:text-dark-100 text-xs font-bold leading-tight text-gray-800">
                                {vendor.label}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedVendorsList(
                                    selectedVendorsList.filter(
                                      (v) => v.id !== vendor.id,
                                    ),
                                  );
                                }}
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                                title="Remove contractor"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                            {Array.isArray(vendor.designations) && vendor.designations.length > 0 ? (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {vendor.designations.map((d) => (
                                  <span
                                    key={d.DesignationId || d.designationId}
                                    className="dark:bg-dark-700 dark:text-gray-300 shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
                                  >
                                    {d.DesignationName || d.designationName}
                                  </span>
                                ))}
                              </div>
                            ) : vendor.designation ? (
                              <p className="mt-1 text-[11px] text-gray-400">
                                {vendor.designation}
                              </p>
                            ) : null}
                            {vendor.externalCompanyName && (
                              <p className="mt-0.5 text-[11px] text-gray-400">
                                {vendor.externalCompanyName}
                              </p>
                            )}
                            {/* Trade type badges */}
                            {Array.isArray(vendor.tradeTypes) &&
                              vendor.tradeTypes.length > 0 &&
                              (() => {
                                const visible = vendor.tradeTypes.slice(0, 2);
                                const extra =
                                  vendor.tradeTypes.length - visible.length;
                                return (
                                  <div className="mt-1.5 flex flex-wrap gap-1">
                                    {visible.map((t, i) => (
                                      <span
                                        key={i}
                                        className="inline-flex items-center rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300"
                                      >
                                        {t}
                                      </span>
                                    ))}
                                    {extra > 0 && (
                                      <span className="dark:bg-dark-700 dark:text-dark-300 dark:border-dark-600 inline-flex items-center justify-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                                        +{extra}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={contractorsEndRef} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 6: Notes & Custom Fields ── */}
        {step === 6 && (
          <div className="space-y-10">
            {/* Notes Section */}
            <div className="space-y-6">
              <Textarea
                label="Internal Notes"
                rows={2}
                placeholder="Private notes for internal team..."
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
              />
              <Textarea
                label="Contractor Instructions"
                rows={2}
                placeholder="Instructions visible to contractors..."
                value={vendorInstructions}
                onChange={(e) => setVendorInstructions(e.target.value)}
              />
            </div>

            {/* Custom Fields Section */}
            <div className="space-y-6 border-t border-gray-100 dark:border-dark-700 pt-6">
              {/* Header + Add button */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-indigo-50 p-2 text-indigo-500 dark:bg-indigo-500/10">
                    <AdjustmentsHorizontalIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                      Custom Fields
                    </h3>
                    <p className="mt-0.5 text-[10px] font-medium text-slate-400">
                      Add and fill custom master fields for this job
                    </p>
                  </div>
                </div>
                <Button
                  variant="soft"
                  color="primary"
                  className="flex h-9 items-center gap-2 px-4 text-[11px] font-bold uppercase"
                  onClick={() => setShowFieldSelectionModal(true)}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add New Field
                </Button>
              </div>

              {/* Selected Custom Fields (with value inputs) */}
              {selectedCustomFields.length === 0 ? (
                <div className="dark:border-dark-700 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 py-14 text-center">
                  <div className="dark:bg-dark-700 mb-3 rounded-2xl bg-slate-100 p-3">
                    <AdjustmentsHorizontalIcon className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="dark:text-dark-400 text-sm font-bold text-slate-500">
                    No custom fields added yet
                  </p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Click &quot;Add New Field&quot; above to select from master
                    fields
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedCustomFields.map((sf) => (
                    <div key={sf.id} className="input-root">
                      {/* Label row — matches app Input label style */}
                      <div className="mb-1.5 flex items-center justify-between">
                        <label className="input-label flex items-center gap-1.5">
                          {sf.label}
                          <span className="text-[9px] font-bold tracking-widest text-slate-400 uppercase">
                            (Optional)
                          </span>
                        </label>
                        <button
                          onClick={() => {
                            setSelectedCustomFields((prev) =>
                              prev.filter((f) => f.id !== sf.id),
                            );
                            try {
                              let saved = JSON.parse(localStorage.getItem("dynamic_fields_pref_2") || "[]");
                              saved = saved.filter(name => name !== sf.fieldName && name !== sf.FieldName);
                              localStorage.setItem("dynamic_fields_pref_2", JSON.stringify(saved));
                            } catch (e) { }
                          }}
                          className="hover:text-error rounded-md p-1 text-slate-400 transition-all hover:bg-rose-50 dark:hover:bg-rose-500/10"
                          title="Remove field"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Input — uses system components where possible */}
                      {sf.type === "textarea" ? (
                        <Textarea
                          rows={3}
                          value={sf.value}
                          onChange={(e) =>
                            setSelectedCustomFields((prev) =>
                              prev.map((f) =>
                                f.id === sf.id
                                  ? { ...f, value: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          placeholder={sf.label}
                        />
                      ) : sf.type === "select" ? (
                        <ReactSelect
                          options={getFieldOptions(sf).map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                          }))}
                          value={
                            sf.value
                              ? (() => {
                                const match = getFieldOptions(sf).find(
                                  (o) => String(o.value) === String(sf.value),
                                );
                                return match
                                  ? { value: match.value, label: match.label }
                                  : { value: sf.value, label: sf.value };
                              })()
                              : null
                          }
                          onChange={(opt) =>
                            setSelectedCustomFields((prev) =>
                              prev.map((f) =>
                                f.id === sf.id
                                  ? { ...f, value: opt?.value || "" }
                                  : f,
                              ),
                            )
                          }
                          placeholder=" Select "
                          className="react-select-container w-full"
                          classNamePrefix="react-select"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                          }}
                        />
                      ) : sf.type === "checkbox" ? (
                        <Checkbox
                          checked={sf.value === "true" || sf.value === true}
                          onChange={(e) =>
                            setSelectedCustomFields((prev) =>
                              prev.map((f) =>
                                f.id === sf.id
                                  ? { ...f, value: String(e.target.checked) }
                                  : f,
                              ),
                            )
                          }
                          label={
                            <span className="dark:text-dark-200 text-sm font-medium text-slate-700">
                              {sf.label}
                            </span>
                          }
                        />
                      ) : sf.type === "attachments" ||
                        sf.type === "file" ||
                        sf.type === "image" ||
                        sf.fieldTypeId === 10 ||
                        sf.fieldTypeId === 16 ||
                        sf.FieldTypeId === 10 ||
                        sf.FieldTypeId === 16 ? (
                        <AttachmentsActions
                          files={
                            Array.isArray(sf.value)
                              ? sf.value
                              : typeof sf.value === "string" && sf.value.trim()
                                ? sf.value
                                  .split(",")
                                  .map((p) => {
                                    const path = p.trim();
                                    return {
                                      name: path.split("/").pop() || path,
                                      path: path,
                                      isExisting: true,
                                    };
                                  })
                                  .filter((f) => f.path)
                                : []
                          }
                          onChange={(files) =>
                            setSelectedCustomFields((prev) =>
                              prev.map((f) =>
                                f.id === sf.id ? { ...f, value: files } : f,
                              ),
                            )
                          }
                          addLabel={`Upload ${sf.label}`}
                          showLabel={false}
                        />
                      ) : (
                        <Input
                          type={
                            sf.type === "number" ||
                              sf.type === "currency" ||
                              sf.type === "percentage"
                              ? "number"
                              : sf.type === "date"
                                ? "date"
                                : sf.type === "email"
                                  ? "email"
                                  : sf.type === "tel"
                                    ? "tel"
                                    : "text"
                          }
                          value={sf.value}
                          onChange={(e) =>
                            setSelectedCustomFields((prev) =>
                              prev.map((f) =>
                                f.id === sf.id
                                  ? { ...f, value: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          placeholder={sf.label}
                          className="h-11"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Step Footer Actions */}
      <div className="dark:border-dark-600 flex shrink-0 items-center justify-between border-t border-gray-100 px-6 py-4">
        <Button
          variant="outlined"
          color="neutral"
          onClick={handleBack}
          disabled={isConverting}
        >
          Back
        </Button>
        <Button
          type="button"
          variant="filled"
          color="primary"
          onClick={step === 6 ? handleSubmit : handleNext}
          disabled={step === 6 && (isConverting || isSubmitting || hasError)}
          className="flex items-center gap-2 px-6 font-bold"
        >
          {step === 6
            ? isConverting || isSubmitting
              ? leadData
                ? "Converting..."
                : "Saving..."
              : leadData
                ? "Convert to Job"
                : "Add Job"
            : "Next"}
          {step < 6 && <ArrowRightIcon className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Overlay Modals & Drawers */}
      <AdvancedSettingsDrawer
        show={showAdvancedSettings}
        onClose={() => setShowAdvancedSettings(false)}
      />
      <AddContactModal
        show={showAddContact}
        onClose={() => setShowAddContact(false)}
        onAdd={handleSelectContact}
        jobId={null}
        leadId={null}
      />
      <AssignContactsToLeadModal
        isOpen={showSelectContact}
        onClose={() => setShowSelectContact(false)}
        initialMode="existing"
        isLocalSelection={true}
        onAssigned={(selectedContacts) => {
          if (selectedContacts) {
            const newContacts = selectedContacts.map((contact) => ({
              id: contact.ContactId || contact.id || contact.Id,
              name:
                contact.FullName ||
                contact.fullName ||
                contact.name ||
                "Unknown",
              fullName: contact.FullName || contact.fullName || contact.name || "Unknown",
              FullName: contact.FullName || contact.fullName || contact.name || "Unknown",
              email: contact.Email || contact.email || "",
              Email: contact.Email || contact.email || "",
              phone:
                contact.PhoneNumber ||
                contact.phoneNumber ||
                contact.phone ||
                "",
              phoneNumber: contact.PhoneNumber || contact.phoneNumber || contact.phone || "",
              PhoneNumber: contact.PhoneNumber || contact.phoneNumber || contact.phone || "",
              abnNumber: contact.ABNNumber || contact.abnNumber || contact.abn || contact.Abn || "",
              companyName: contact.CompanyName || contact.companyName || contact.company_name || "",
              streetAddress: contact.StreetAddress || contact.streetAddress || contact.address || contact.Address || "",
              suburb: contact.Suburb || contact.suburb || "",
              city: contact.City || contact.city || "",
              state: contact.State || contact.state || "",
              postalCode: contact.PostalCode || contact.postalCode || contact.postCode || contact.postcode || "",
            }));
            setClientsList(newContacts);
          }
          setShowSelectContact(false);
        }}
        leadId={leadData?.id || leadData?.leadId || null}
        jobId={null}
        existingContacts={clientsList.map((c) => ({
          id: c.id,
          ContactId: c.id,
          fullName: c.name || c.fullName,
          FullName: c.name || c.FullName,
          email: c.email || c.Email,
          Email: c.email || c.Email,
          phoneNumber: c.phone || c.phoneNumber,
          PhoneNumber: c.phone || c.PhoneNumber,
          abnNumber: c.abnNumber || c.ABNNumber,
          companyName: c.companyName || c.CompanyName,
          streetAddress: c.streetAddress || c.StreetAddress,
          StreetAddress: c.streetAddress || c.StreetAddress,
          suburb: c.suburb || c.Suburb,
          Suburb: c.suburb || c.Suburb,
          city: c.city || c.City,
          City: c.city || c.City,
          state: c.state || c.State,
          State: c.state || c.State,
          postalCode: c.postalCode || c.PostalCode,
          PostalCode: c.postalCode || c.PostalCode,
        }))}
      />
      <ContactModal
        isOpen={showEditContactModal}
        onClose={() => {
          setShowEditContactModal(false);
          setEditingContact(null);
        }}
        contactData={editingContact}
        onSuccess={(updatedPayload) => {
          if (editingContact) {
            setClientsList((prev) =>
              prev.map((c) => {
                const cId = String(c.id || c.contactId || c.ContactId || c.Id || "");
                const eId = String(editingContact.id || editingContact.contactId || editingContact.ContactId || editingContact.Id || "");
                const uId = String(updatedPayload?.id || updatedPayload?.contactId || updatedPayload?.ContactId || "");
                if ((cId && eId && cId === eId) || (cId && uId && cId === uId)) {
                  const newName = updatedPayload?.fullName || updatedPayload?.FullName || updatedPayload?.name || editingContact?.fullName || editingContact?.FullName || c.name || c.fullName;
                  const newEmail = updatedPayload?.email ?? updatedPayload?.Email ?? c.email ?? c.Email;
                  const newPhone = updatedPayload?.phoneNumber ?? updatedPayload?.PhoneNumber ?? c.phone ?? c.phoneNumber;
                  const newCompany = updatedPayload?.companyName ?? updatedPayload?.CompanyName ?? c.companyName ?? c.CompanyName;
                  const newAbn = updatedPayload?.abnNumber ?? updatedPayload?.ABNNumber ?? c.abnNumber ?? c.abn;
                  const newStreet = updatedPayload?.streetAddress ?? updatedPayload?.StreetAddress ?? c.streetAddress;
                  const newSuburb = updatedPayload?.suburb ?? updatedPayload?.Suburb ?? c.suburb;
                  const newPostal = updatedPayload?.postalCode ?? updatedPayload?.PostalCode ?? c.postalCode;
                  const newState = updatedPayload?.state ?? updatedPayload?.State ?? c.state;

                  return {
                    ...c,
                    name: newName,
                    fullName: newName,
                    FullName: newName,
                    email: newEmail,
                    Email: newEmail,
                    phone: newPhone,
                    phoneNumber: newPhone,
                    PhoneNumber: newPhone,
                    companyName: newCompany,
                    CompanyName: newCompany,
                    abnNumber: newAbn,
                    ABNNumber: newAbn,
                    streetAddress: newStreet,
                    StreetAddress: newStreet,
                    suburb: newSuburb,
                    Suburb: newSuburb,
                    postalCode: newPostal,
                    PostalCode: newPostal,
                    state: newState,
                    State: newState,
                  };
                }
                return c;
              })
            );
          }
          setShowEditContactModal(false);
          setEditingContact(null);
        }}
      />
      <ManageJobTypesModal
        show={showManageJobTypes}
        onClose={() => setShowManageJobTypes(false)}
      />

      {/* Field Selection Modal (Custom Fields) */}
      <FieldSelectionModal
        isOpen={showFieldSelectionModal}
        onClose={() => setShowFieldSelectionModal(false)}
        masterFields={masterFieldCategories}
        masterFieldsLoading={masterFieldsLoading}
        onSelectField={(fieldData) => {
          const newField = {
            id: fieldData.masterFieldId || fieldData.id || Date.now(),
            label: fieldData.fieldLabel || fieldData.label || "",
            fieldName: fieldData.fieldName || "",
            type: fieldData.htmlInputType || fieldData.type || "text",
            options: fieldData.options || [],
            value: fieldData.defaultValue || "",
            FieldTypeId:
              fieldData.fieldTypeId ||
              (fieldData.htmlInputType === "select" ? 7 : 1),
            fieldTypeId:
              fieldData.fieldTypeId ||
              (fieldData.htmlInputType === "select" ? 7 : 1),
            DefaultValue: fieldData.defaultValue || "",
            defaultValue: fieldData.defaultValue || "",
            MasterFieldId: fieldData.masterFieldId || fieldData.id,
            masterFieldId: fieldData.masterFieldId || fieldData.id,
            FieldName: fieldData.fieldName || "",
            FieldLabel: fieldData.fieldLabel || fieldData.label || "",
            MasterFormFieldOptions: fieldData.options || [],
            CategoryId: fieldData.CategoryId,
            CategoryName: fieldData.CategoryName,
          };
          setSelectedCustomFields((prev) => {
            if (prev.some((f) => f.id === newField.id)) return prev;

            try {
              const saved = JSON.parse(localStorage.getItem("dynamic_fields_pref_2") || "[]");
              if (newField.fieldName && !saved.includes(newField.fieldName)) {
                saved.push(newField.fieldName);
                localStorage.setItem("dynamic_fields_pref_2", JSON.stringify(saved));
              }
            } catch (e) { }

            return [...prev, newField];
          });
        }}
        onRefreshMasterFields={fetchMasterFieldCategories}
        isFieldAdded={(masterField) =>
          selectedCustomFields.some((sf) => sf.id === masterField.Id)
        }
      />
      <Transition appear show={showManageStatuses} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="scrollbar-sm relative flex flex-col rounded-2xl bg-white dark:bg-dark-700 overflow-hidden shadow-xl text-left transition-all duration-300 w-full max-w-2xl">
              <ManageStatusContent
                onClose={() => setShowManageStatuses(false)}
                statuses={jobStatuses}
                setStatuses={setJobStatuses}
                reloadStatuses={(silent) => handleFetchStatuses(silent)}
              />
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
      <ConfirmModal
        show={showDesignationConfirmModal}
        closeOnOutsideClick={false}
        onClose={() => {
          setShowDesignationConfirmModal(false);
          setHasSkippedDesignationModal(true);
          setStep(6);
        }}
        onOk={() => {
          setShowDesignationConfirmModal(false);
        }}
        buttonColor="primary"
        messages={{
          pending: {
            title: "Multiple Designations Selected",
            description: "Selected internal users have multiple designations. Please choose designation for this job.",
            actionText: "OK",
            cancelText: "Skip",
          },
        }}
      />
    </div>
  );
}
